import React, { createRef, KeyboardEvent, RefObject } from "react";
import { withApollo } from "@apollo/client/react/hoc";
import {
  WebGL1Renderer,
  PerspectiveCamera,
  Scene,
  Color,
  HemisphereLight,
  MathUtils,
  Vector3,
  DoubleSide,
  Box3,
  LoadingManager,
  Group,
  Object3D,
  MeshPhongMaterial,
  PlaneGeometry,
  Mesh,
  TextureLoader,
  RepeatWrapping,
  Cache,
  Raycaster,
  Vector2,
  Texture,
  Spherical,
  LineBasicMaterial,
  BufferGeometry,
  Line,
  SpotLight,
  SpotLightHelper,
  CameraHelper,
  // CubeTextureLoader,
} from "three";
import { Group as GroupState } from "../Store/ConstructorReducer";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader";
import Controls from "./Controls";
import {
  Box,
  createStandaloneToast,
  Heading,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import Loader from "./Helpers/Loader";
import { ApolloClient, gql, InMemoryCache } from "@apollo/client";
import {
  clearMovingNodes,
  setMovingNode,
  toggleMovingNode,
  clearTypes,
  setType,
  setId,
  clearIds,
  setRotation,
  clearRotations,
  setAction,
  setGroup,
  clearGroups,
  setObjectInfo,
  setScales,
  clearScales,
  setSaveProportions,
  clearSaveProportions,
  setDeletedId,
  clearDeletedIds,
  setDeletedType,
  clearDeletedTypes,
  setDeletedRotation,
  clearDeletedRotations,
  setDeletedScale,
  clearDeletedScales,
  setDeletedGroup,
  clearDeletedGroups,
  setTexture,
  setDeletedTexture,
  clearTextures,
  clearDeletedTextures,
} from "../Store/ConstructorReducer";
import { setModalOpen, ModalsName } from "../Store/ModalsReducer";
import {
  LOAD_PROJECT,
  LOAD_GROUP,
  SAVE_PROJECT,
  LOAD_PROJECTS,
} from "./Queries";
import type { RootState } from "../Store/store";
import { connect, ConnectedProps } from "react-redux";
import LoaderFullwidth from "./Helpers/LoaderFullwidth";
import { setLoading } from "../Store/GlobalReducer";
import { srcToFile } from "../Utils";
import { ProjectPayload } from "./Modals/Projects/Types";
import { ItemInfo } from "./Modals/ModalAdd/CoveringMaster";
import {
  Action,
  ActionTypes,
  addAction,
  clearActionsHistory,
} from "../Store/ActionsHistoryReducer";

const mapState = (state: RootState) => ({
  movingNodes: state.main.movingNodes,
  hoverNode: state.main.hoverNode,
  types: state.main.types,
  ids: state.main.ids,
  fastMove: state.main.fastMove,
  projectId: state.global.projectId,
  rotations: state.main.rotations,
  modalAddOpen: state.modals[ModalsName.ADD].isOpen,
  loading: state.global.loading,
  action: state.main.action,
  groups: state.main.groups,
  scales: state.main.scales,
  focusLocked: state.global.focusLocked,
  saveProportions: state.main.saveProportions,
  blockedItems: state.main.blockedItems,
  actionsIndex: state.actionsHistory.actionsIndex,
  actionsHistory: state.actionsHistory.actions,
  nativeActionSet: state.actionsHistory.native,
  deletedItems: state.main.deletedItems,
  textures: state.main.textures,
});

const mapDispatch = {
  clearMovingNodes,
  setMovingNode,
  toggleMovingNode,
  setType,
  clearTypes,
  setId,
  clearIds,
  setRotation,
  clearRotations,
  setLoading,
  setAction,
  setGroup,
  clearGroups,
  setObjectInfo,
  setModalOpen,
  clearScales,
  setScales,
  clearSaveProportions,
  setSaveProportions,
  addAction,
  setDeletedId,
  clearDeletedIds,
  setDeletedRotation,
  clearDeletedRotations,
  setDeletedType,
  clearDeletedTypes,
  setDeletedScale,
  clearDeletedScales,
  clearActionsHistory,
  setDeletedGroup,
  clearDeletedGroups,
  setTexture,
  setDeletedTexture,
  clearTextures,
  clearDeletedTextures,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

declare global {
  interface Window {
    instance: any;
    scene: Scene;
  }
}

Cache.enabled = true;

type PreviewProps = PropsFromRedux & {
  client?: ApolloClient<InMemoryCache>;
  canvasRef: RefObject<HTMLCanvasElement>;
  compassRef: RefObject<HTMLDivElement>;
};

type PreviewState = {
  loading: boolean;
  nodeItems: { [key: string]: Object3D };
  deletedItems: { [key: string]: Object3D };
  // offsets: { [key: string]: { x: number; y: number; z: number } };
  initialized: boolean;
  preloaderVisible: boolean;
  colors: { skyColor: number; groundColor: number };
  intensity: {
    value: number;
    changed: boolean;
  };
  time: number;
};

Mesh.prototype.copy = function (source: any) {
  Object3D.prototype.copy.call(this, source);

  if (source.morphTargetInfluences !== undefined) {
    this.morphTargetInfluences = source.morphTargetInfluences.slice();
  }

  if (source.morphTargetDictionary !== undefined) {
    this.morphTargetDictionary = Object.assign(
      {},
      source.morphTargetDictionary
    );
  }

  if (source.material.clone) this.material = source.material.clone();
  if (source.geometry.clone) this.geometry = source.geometry.clone();

  return this;
};

class Preview extends React.Component<PreviewProps, PreviewState> {
  private renderer: WebGL1Renderer | null = null;
  private camera: PerspectiveCamera | null = null;
  private controls: OrbitControls | null = null;
  private scene: Scene = new Scene();

  private light: HemisphereLight | null = null;
  private spotlight: SpotLight | null = null;
  private spothelper: SpotLightHelper | null = null;
  private frame: number = -1;
  private raycaster: Raycaster = new Raycaster();
  private containerRef: RefObject<HTMLDivElement> = createRef();
  offsets: { [key: string]: { x: number; y: number; z: number } } = {};
  deletedOffsets: { [key: string]: { x: number; y: number; z: number } } = {};

  private toast: any = null;

  private lines: Object3D[] = [];

  private moveDistance: number = 0;
  private moveAxis: "x" | "y" | "z" | "" = "";

  constructor(props: PreviewProps) {
    super(props);
    this.state = {
      loading: false,
      nodeItems: {},
      deletedItems: {},
      // offsets: {},
      initialized: false,
      preloaderVisible: false,
      colors: { skyColor: 0xffffff, groundColor: 0x000000 },
      intensity: { value: 4, changed: false },
      time: 0
    };
    window.instance = this;

    this.handleKeydown = this.handleKeydown.bind(this);

    this.toast = createStandaloneToast();
  }
  handleKeydown(ev: KeyboardEvent) {
    if (this.props.modalAddOpen || this.props.focusLocked) return;
    let move = "";
    if (ev.code === "KeyA" || ev.key === "ArrowLeft") {
      ev.preventDefault();
      this.moveNodes("x", -1 * (this.props.fastMove ? 50 : 1));
      move = "left";
      this.moveAxis = "x";
      this.moveDistance += -1 * (this.props.fastMove ? 50 : 1);
    }
    if (ev.code === "KeyD" || ev.key === "ArrowRight") {
      ev.preventDefault();
      this.moveNodes("x", 1 * (this.props.fastMove ? 50 : 1));
      move = "right";
      this.moveAxis = "x";
      this.moveDistance += 1 * (this.props.fastMove ? 50 : 1);
    }
    if (ev.code === "KeyW" || ev.key === "ArrowUp") {
      ev.preventDefault();
      this.moveNodes("z", -1 * (this.props.fastMove ? 50 : 1));
      move = "up";
      this.moveAxis = "z";
      this.moveDistance += -1 * (this.props.fastMove ? 50 : 1);
    }
    if (ev.code === "KeyS" || ev.key === "ArrowDown") {
      ev.preventDefault();
      this.moveNodes("z", 1 * (this.props.fastMove ? 50 : 1));
      move = "down";

      this.moveAxis = "z";
      this.moveDistance += 1 * (this.props.fastMove ? 50 : 1);
    }
    if (ev.code === "KeyG") {
      ev.preventDefault();
      this.moveNodes("y", -1 * (this.props.fastMove ? 50 : 1));
      move = "bottom";

      this.moveAxis = "y";
      this.moveDistance += -1 * (this.props.fastMove ? 50 : 1);
    }
    if (ev.code === "KeyT") {
      ev.preventDefault();
      this.moveNodes("y", 1 * (this.props.fastMove ? 50 : 1));
      move = "top";
      this.moveAxis = "y";
      this.moveDistance += 1 * (this.props.fastMove ? 50 : 1);
    }
    if (ev.code === "KeyE") {
      ev.preventDefault();
      this.rotateNodes(90);
    }
    if (ev.code === "KeyQ") {
      ev.preventDefault();
      this.rotateNodes(-90);
    }
    if (move !== this.props.action) {
      this.props.setAction(move);
    }
  }
  componentDidMount() {
    if (this.state.initialized) return;
    if (!this.props.canvasRef || !this.props.canvasRef.current)
      throw new Error("No canvas");
    if (!this.containerRef || !this.containerRef.current)
      throw new Error("Cannot find canvas container");
    const el = this.containerRef.current;
    const canvas = this.props.canvasRef.current as HTMLCanvasElement;
    canvas.width = el.clientWidth;
    canvas.height = el.clientHeight;
    this.init();
    document.addEventListener("keydown", this.handleKeydown as any);
    document.addEventListener("keyup", () => {
      this.props.setAction("");
      if (this.moveAxis !== "") {
        const action: Action = {
          type: ActionTypes.SET_OFFSET,
          data: {
            keys: this.props.movingNodes,
            offset: {
              axis: this.moveAxis,
              distance: this.moveDistance,
            },
          },
        };
        this.moveAxis = "";
        this.moveDistance = 0;
        this.props.addAction(action);
      }
    });
    if (this.props.canvasRef.current) {
      this.props.canvasRef.current.addEventListener(
        "click",
        this.debounce(this.raycast.bind(this))
      );
    }
    this.props.canvasRef.current.focus();
  }

  raycast(ev: MouseEvent) {
    if (
      !this.containerRef ||
      !this.containerRef.current ||
      this.props.action !== ""
    )
      return;
    let mouse = new Vector2();
    const el: HTMLDivElement | null = this.containerRef.current;
    if (!el || !this.props.canvasRef.current) return;

    const clientRect = this.props.canvasRef.current.getBoundingClientRect();
    let x =
      ((ev.clientX - clientRect.left) * this.props.canvasRef.current.width) /
      clientRect.width;
    let y =
      ((ev.clientY - clientRect.top) * this.props.canvasRef.current.height) /
      clientRect.height;
    // if (process.env.NODE_ENV !== "development")
    y -= 64; // header
    mouse.x = (x / this.props.canvasRef.current.width) * 2 - 1;
    mouse.y = -(y / this.props.canvasRef.current.height) * 2 + 1;

    if (this.camera === null || this.renderer === null || !this.scene) return;
    this.raycaster.setFromCamera(mouse, this.camera);

    const intersects = this.raycaster
      .intersectObjects(this.scene.children, true)
      .sort((a, b) => {
        // Сортируем чтобы получить ближайшее пересечение
        if (!a || !b || !a.faceIndex || !b.faceIndex) return 0;
        return a.faceIndex > b.faceIndex ? 1 : -1;
      });
    let res: string = "";
    for (let i = 0; i < intersects.length; i++) {
      for (const key in this.state.nodeItems) {
        const node = this.state.nodeItems[key];
        if (node.uuid === intersects[i].object.parent?.uuid) res = key;
        if (
          intersects[i].object.parent &&
          intersects[i].object.parent?.parent
        ) {
          if (node.uuid === intersects[i].object.parent?.parent?.uuid)
            res = key;
        }
      }
    }
    if (res !== "") {
      if (this.props.blockedItems.indexOf(res) !== -1) return;
      // this.props.toggleMovingNode(res);
      // this.clearLines();
      // this.drawHelper();
    } else {
      this.props.clearMovingNodes();
      this.clearLines();
    }
    this.renderer.render(this.scene, this.camera);
  }

  processObjectInfo(keys: string | string[] = this.props.movingNodes) {
    const info: { [key: string]: { x: number; y: number; z: number } } = {};
    for (const key of keys) {
      const node = this.state.nodeItems[key];
      const box = new Box3().setFromObject(node);
      const size = new Vector3();
      box.getSize(size);
      info[key] = {
        x: size.x,
        y: size.y,
        z: size.z,
      };
    }
    this.props.setObjectInfo(info);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeydown as any);
    this.props.clearIds({});
    this.props.clearMovingNodes();
    this.props.clearRotations({});
    this.props.clearTypes({});
    this.offsets = {};
    this.setState({ nodeItems: {} });
  }

  setStateAsync(args: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.setState(args, () => {
        resolve();
      });
    });
  }

  setPos(uuid: string, offsets: { x: number; y: number; z: number }) {
    this.scene.traverse((child) => {
      if (child.uuid === uuid) {
        if (offsets.x) child.position.x += offsets.x;
        if (offsets.y) child.position.y += offsets.y;
        if (offsets.z) child.position.z += offsets.z;
      }
    });
  }

  async processGroup(group: {
    name: string;
    types: { [key: string]: string };
    ids: { [key: string]: number };
    offsets: { [key: string]: { x: 0; y: 0; z: 0 } };
    rotations: { [key: string]: number };
    models: { [key: string]: any };
  }): Promise<string | void> {
    const items: string[] = [];
    for (const key in group.ids) {
      if (group.types[key] === "group") return;
      const uuid = await this.loadObjects(
        `${group.models[key].path3D.obj}`,
        `${group.models[key].path3D.mtl}`,
        group.types[key],
        group.ids[key]
      );
      items.push(uuid);
      const offsets = group.offsets[key];
      const { nodeItems } = this.state;
      if (group.rotations[key]) {
        const angle = (group.rotations[key] * Math.PI) / 180;
        const mesh: Object3D = nodeItems[uuid];
        if (!mesh) return;
        mesh.rotateY(angle);
        const bb = new Box3().setFromObject(mesh);
        let dx = -bb.max.x + -(bb.min.x - bb.max.x) / 2;
        let dz = -bb.max.z + -(bb.min.z - bb.max.z) / 2;
        mesh.position.x += dx;
        mesh.position.z += dz;
        this.props.setRotation({ key: uuid, angle: group.rotations[key] });
      }

      this.setPos(uuid, offsets);

      this.offsets[uuid] = { ...offsets };
    }

    const groupState: GroupState = {
      name: group.name,
      types: {},
      ids: {},
      rotations: {},
      offsets: {},
      scales: {},
      textures: {},
    };

    const newGroup = new Group();

    const parts: Object3D[] = [];

    for (const key of items) {
      if (this.state.nodeItems[key]) parts.push(this.state.nodeItems[key]);
      if (this.offsets[key]) {
        groupState.offsets[key] = this.offsets[key];
        delete this.offsets[key];
      }
      if (this.props.ids[key]) {
        groupState.ids[key] = this.props.ids[key];
        this.props.clearIds({ key });
      }
      if (this.props.types[key]) {
        groupState.types[key] = this.props.types[key];
        this.props.clearTypes({ key });
      }
      if (this.props.scales[key]) {
        groupState.scales[key] = this.props.scales[key];
        this.props.clearScales({ key });
      }

      if (this.props.textures[key]) {
        groupState.textures[key] = this.props.textures[key];
        this.props.clearTextures({ key });
      }
      if (Object.keys(this.props.rotations).indexOf(key) !== -1) {
        groupState.rotations[key] = this.props.rotations[key];
        this.props.clearRotations({ key });
      }
    }

    const newNodes: { [key: string]: Object3D } = {};
    for (const key in this.state.nodeItems) {
      if (items.indexOf(key) === -1) newNodes[key] = this.state.nodeItems[key];
    }

    this.props.clearMovingNodes();
    this.props.setType({ key: newGroup.uuid, type: "group" });
    this.props.setSaveProportions({ key: newGroup.uuid, value: false });
    this.props.setScales({ key: newGroup.uuid, scale: { x: 1, y: 1, z: 1 } });
    this.props.setGroup({ key: newGroup.uuid, group: groupState });

    this.offsets[newGroup.uuid] = { x: 0, y: 0, z: 0 };
    this.props.setRotation({ key: newGroup.uuid, angle: 0 });

    for (const model of parts) newGroup.add(model);

    this.setState({ nodeItems: { ...newNodes, [newGroup.uuid]: newGroup } });

    this.scene.add(newGroup);
    return newGroup.uuid;
  }

  async replaceProject(id: number) {
    if (!this.props.client) throw new Error("No Apollo client");
    this.setState({ loading: true });
    const { data, error } = await this.props.client.query({
      query: LOAD_PROJECT,
      variables: { id },
    });
    if (error) {
      this.toast({
        status: "error",
        position: "top-right",
        variant: "left-accent",
        title: `Ошибка: ${error.message}`,
      });
      return;
    }
    if (!data) throw new Error("No project data");

    for (const key in this.state.nodeItems) {
      this.scene.remove(this.state.nodeItems[key]);
    }
    this.props.clearGroups({});
    this.props.clearScales({});
    this.props.clearTextures({});
    this.props.clearRotations({});
    this.props.clearIds({});
    this.props.clearSaveProportions({});
    this.props.clearTypes({});
    this.offsets = {};
    this.setState({ nodeItems: {} });

    const info = data.project;

    const groups = info.groups.map((group: any) => JSON.parse(group));

    let modelsIndex = 0;
    let groupIndex = 0;
    const models: any = [];
    for (var i = 0; i < info.types.length; i++) {
      if (info.types[i] === "group") {
        const group = groups[groupIndex++];
        const modelsTmp = [];
        for (const item of group.types) {
          modelsTmp.push(info.models[modelsIndex++]);
        }
        models.push(modelsTmp);
      } else {
        models.push(info.models[modelsIndex++]);
      }
    }

    const processGroup = async (args: {
      group: {
        name: string;
        models: any[];
        ids: number[];
        types: string[];
        offsets: { x: number; y: number; z: number }[];
        rotations: number[];
        scales: { x: number; y: number; z: number }[];
        textures: string[];
      };
      offset: { x: number; y: number; z: number };
      scale: { x: number; y: number; z: number };
      rotation: number;
    }): Promise<string[]> => {
      const models: {
        id: number;
        name: string;
        path3D: {
          dirname: string;
          obj: string;
          mtl: string;
        };
      }[] = [];
      for (const key in args.group.models) {
        models.push(args.group.models[key]);
      }

      console.log(models);
      const addedObjects: string[] = [];
      // this.props.clearMovingNodes();
      for (let i = 0; i < models.length; i++) {
        const model = args.group.models[i];
        const key = await this.loadObjects(
          `/${model.path3D.dirname}/${model.path3D.obj}`,
          `/${model.path3D.dirname}/${model.path3D.mtl}`,
          args.group.types[i],
          args.group.ids[i]
        );
        if(args.group.textures && args.group.textures[i] && args.group.textures[i].length > 0) await this.changeTexture([key], args.group.textures[i], false);
        addedObjects.push(key);
        const angle = (args.group.rotations[i] * Math.PI) / 180;
        const mesh: Object3D = this.state.nodeItems[key];
        if (!mesh) throw new Error("No group to rotate");
        mesh.rotateY(angle);
        const bb = new Box3().setFromObject(mesh);
        let dx = -bb.max.x + -(bb.min.x - bb.max.x) / 2;
        let dz = -bb.max.z + -(bb.min.z - bb.max.z) / 2;
        mesh.position.x += dx;
        mesh.position.z += dz;
        this.props.setRotation({ key, angle: args.group.rotations[i] });
        const offsets = args.group.offsets[i];
        if (offsets.x) this.state.nodeItems[key].position.x += offsets.x;
        if (offsets.y) this.state.nodeItems[key].position.y += offsets.y;
        if (offsets.z) this.state.nodeItems[key].position.z += offsets.z;
        this.offsets[key] = { ...offsets };

        this.state.nodeItems[key].scale.set(
          args.group.scales[i].x,
          args.group.scales[i].y,
          args.group.scales[i].z
        );
        // this.props.setScales({ key, scale: args.group.scales[i] });
      }
      this.props.toggleMovingNode(addedObjects);

      const groupKey = this.handleGroup(args.group.name);

      const angle = (args.rotation * Math.PI) / 180;
      if (angle !== 0) {
        const mesh: Object3D = this.state.nodeItems[groupKey];
        if (!mesh) throw new Error("No mesh to rotate");
        mesh.rotateY(angle);
        const bb = new Box3().setFromObject(mesh);
        let dx = -bb.max.x + -(bb.min.x - bb.max.x) / 2;
        let dz = -bb.max.z + -(bb.min.z - bb.max.z) / 2;
        mesh.position.x += dx;
        mesh.position.z += dz;
        this.props.setRotation({ key: groupKey, angle: args.rotation });
      }
      if (args.offset.x)
        this.state.nodeItems[groupKey].position.x += args.offset.x;
      if (args.offset.y)
        this.state.nodeItems[groupKey].position.y += args.offset.y;
      if (args.offset.z)
        this.state.nodeItems[groupKey].position.z += args.offset.z;
      this.offsets[groupKey] = { ...args.offset };

      this.state.nodeItems[groupKey].scale.set(
        args.scale.x,
        args.scale.y,
        args.scale.z
      );
      // this.props.setScales({ key: groupKey, scale: args.scale });
      return addedObjects;
    };

    const process = async (args: {
      id: number;
      type: string;
      texture: string;
      rotation: number;
      offsets: { x: number; y: number; z: number };
      model: {
        path3D: {
          dirname: string;
          obj: string;
          mtl: string;
        };
      };
      scale: { x: number; y: number; z: number };
    }): Promise<string> => {
      const key = await this.loadObjects(
        `${args.model.path3D.dirname}/${args.model.path3D.obj}`,
        `${args.model.path3D.dirname}/${args.model.path3D.mtl}`,
        args.type,
        args.id
      );
      if(args.texture && args.texture.length > 0) await this.changeTexture([key], args.texture, false);
      const angle = (args.rotation * Math.PI) / 180;
      const mesh: Object3D = this.state.nodeItems[key];
      if (!mesh) throw new Error("No mesh to rotate");
      mesh.rotateY(angle);
      const bb = new Box3().setFromObject(mesh);
      let dx = -bb.max.x + -(bb.min.x - bb.max.x) / 2;
      let dz = -bb.max.z + -(bb.min.z - bb.max.z) / 2;
      mesh.position.x += dx;
      mesh.position.z += dz;
      this.props.setRotation({ key, angle: args.rotation });
      this.state.nodeItems[key].scale.set(
        args.scale.x,
        args.scale.y,
        args.scale.z
      );
      this.center(mesh);
      const offsets = args.offsets;
      if (offsets.x) this.state.nodeItems[key].position.x += offsets.x;
      if (offsets.y) this.state.nodeItems[key].position.y += offsets.y;
      if (offsets.z) this.state.nodeItems[key].position.z += offsets.z;
      this.offsets[key] = { ...offsets };

      // this.props.setScales({ key, scale: args.scale });
      return key;
    };

    let groupsInd = 0;
    for (i = 0; i < info.types.length; i++) {
      const type = info.types[i];
      if (type === "group")
        await processGroup({
          group: groups[groupsInd++],
          offset: info.offsets[i],
          scale: info.scales[i],
          rotation: info.rotations[i],
        });
      else
        await process({
          type: info.types[i],
          id: info.ids[i],
          offsets: info.offsets[i],
          scale: info.scales[i],
          rotation: info.rotations[i],
          model: models[i],
          texture: info.textures[i]
        });
    }
    // await Promise.all(promises);

    const { position, rotation } = info.camera;
    if (!this.camera) throw new Error("Camera not initialized yet");
    this.camera.position.set(position.x, position.y, position.z);
    this.camera.rotation.set(rotation.x, rotation.y, rotation.z);

    this.setState({ loading: false });
    this.props.clearActionsHistory({ replace: true });
    this.toast({
      status: "success",
      variant: "left-accent",
      position: "top-right",
      title: "Проект загружен!",
    });
  }

  componentDidUpdate(prevProps: PreviewProps) {
    if (prevProps.projectId !== this.props.projectId) {
      this.replaceProject(this.props.projectId);
    }
    if (
      this.props.actionsIndex !== -1 &&
      this.props.actionsIndex !== prevProps.actionsIndex &&
      !this.props.nativeActionSet
    ) {
      if (this.props.actionsIndex > prevProps.actionsIndex) {
        this.processRedo(prevProps.actionsIndex);
      } else {
        this.processUndo(prevProps.actionsIndex);
      }
    }
    if (prevProps.movingNodes.length !== this.props.movingNodes.length) {
      this.drawHelper();
      this.processObjectInfo();
    }
    for (const key of this.props.movingNodes) {
      // if (!this.props.textures[key] || !prevProps.textures[key]) continue;
      if (
        prevProps.textures[key] !== this.props.textures[key] &&
        this.props.textures[key] !== ""
      )
        this.changeTexture(key, this.props.textures[key]);
    }
    for (const key of this.props.movingNodes) {
      if (!this.props.scales[key] || !prevProps.scales[key]) continue;
      if (prevProps.scales[key].x !== this.props.scales[key].x)
        this.handleScale("x", this.props.scales[key].x, key);
      if (prevProps.scales[key].y !== this.props.scales[key].y)
        this.handleScale("y", this.props.scales[key].y, key);
      if (prevProps.scales[key].z !== this.props.scales[key].z)
        this.handleScale("z", this.props.scales[key].z, key);
    }
  }

  processUndo(prevIndex: number = this.props.actionsHistory.length - 1) {
    for (let index = prevIndex; index > this.props.actionsIndex; index--) {
      const action = this.props.actionsHistory[index];
      this.props.clearMovingNodes();
      if (!action) continue;
      switch (action.type) {
        case ActionTypes.MIRROR: {
          if (action.data.mirror && action.data.mirror.axis)
            this.handleMirror(action.data.mirror.axis, action.data.keys, false);
          break;
        }
        case ActionTypes.SET_ROTATION: {
          if (action.data.rotate) {
            console.log(action.data.rotate.from - action.data.rotate.to);
            this.handleRotate(
              action.data.rotate.from - action.data.rotate.to,
              action.data.keys,
              false
            );
          }
          break;
        }
        case ActionTypes.SET_OFFSET: {
          if (action.data.offset) {
            this.moveNodes(
              action.data.offset.axis,
              -action.data.offset.distance,
              action.data.keys
            );
          }
          break;
        }
        case ActionTypes.ADD_MODEL: {
          if (action.data.model) {
            let keys = action.data.keys;
            if (!Array.isArray(keys)) keys = [keys];
            for (const key of keys) {
              const node = this.state.nodeItems[key];
              node.visible = false;
            }
          }
          break;
        }
        case ActionTypes.REMOVE_MODEL: {
          let keys = action.data.keys;
          if (!Array.isArray(keys)) keys = [keys];
          const { nodeItems, deletedItems } = this.state;
          const nodeItemsTmp = nodeItems;
          const deletedItemsTmp = deletedItems;
          for (const key of keys) {
            nodeItemsTmp[key] = deletedItemsTmp[key];
            delete deletedItemsTmp[key];

            this.offsets[key] = this.deletedOffsets[key];
            delete this.deletedOffsets[key];

            this.props.setId({ key, id: this.props.deletedItems.ids[key] });
            this.props.clearDeletedIds({ key });

            this.props.setType({
              key,
              type: this.props.deletedItems.types[key],
            });
            this.props.clearDeletedTypes({ key });

            this.props.setTexture({
              key,
              texture: this.props.deletedItems.textures[key],
            });
            this.props.clearDeletedTextures({ key });

            this.props.setRotation({
              key,
              angle: this.props.deletedItems.rotations[key],
            });
            this.props.clearDeletedRotations({ key });

            this.props.setScales({
              key,
              scale: this.props.deletedItems.scales[key],
            });
            this.props.clearDeletedScales({ key });

            nodeItemsTmp[key].visible = true;
          }
          this.setState({
            nodeItems: nodeItemsTmp,
            deletedItems: deletedItemsTmp,
          });
          break;
        }
        case ActionTypes.REPLACE_MODEL: {
          if (Array.isArray(action.data.keys)) {
            const [oldKey, newKey] = action.data.keys;
            const { nodeItems, deletedItems } = this.state;
            const nodeItemsTmp = nodeItems;
            const deletedItemsTmp = deletedItems;

            deletedItemsTmp[oldKey].visible = true;
            nodeItemsTmp[oldKey] = deletedItemsTmp[oldKey];
            delete deletedItemsTmp[oldKey];

            nodeItemsTmp[newKey].visible = false;
            deletedItemsTmp[newKey] = nodeItemsTmp[newKey];
            delete nodeItemsTmp[newKey];

            // newKey -> deleted
            // oldKey -> state

            this.offsets[oldKey] = this.deletedOffsets[oldKey];
            delete this.deletedOffsets[oldKey];
            this.deletedOffsets[newKey] = this.offsets[newKey];
            delete this.offsets[newKey];

            this.props.setId({
              key: oldKey,
              id: this.props.deletedItems.ids[oldKey],
            });
            this.props.setDeletedId({
              key: newKey,
              value: this.props.ids[newKey],
            });
            this.props.clearIds({ key: newKey });
            this.props.clearDeletedIds({ key: oldKey });

            this.props.setType({
              key: oldKey,
              type: this.props.deletedItems.types[oldKey],
            });
            this.props.setDeletedType({
              key: newKey,
              value: this.props.types[newKey],
            });
            this.props.clearTypes({ key: newKey });
            this.props.clearDeletedTypes({ key: oldKey });

            this.props.setTexture({
              key: oldKey,
              texture: this.props.deletedItems.textures[oldKey],
            });
            this.props.setDeletedTexture({
              key: newKey,
              texture: this.props.types[oldKey],
            });
            this.props.clearTextures({ key: newKey });
            this.props.clearDeletedTextures({ key: oldKey });

            this.props.setRotation({
              key: oldKey,
              angle: this.props.deletedItems.rotations[oldKey],
            });
            this.props.setDeletedRotation({
              key: newKey,
              value: this.props.rotations[newKey],
            });
            this.props.clearRotations({ key: newKey });
            this.props.clearDeletedRotations({ key: oldKey });

            this.props.setScales({
              key: oldKey,
              scale: this.props.deletedItems.scales[oldKey],
            });
            this.props.setDeletedScale({
              key: newKey,
              value: this.props.scales[newKey],
            });
            this.props.clearScales({ key: newKey });
            this.props.clearDeletedScales({ key: oldKey });

            this.setState({
              nodeItems: nodeItemsTmp,
              deletedItems: deletedItemsTmp,
            });
          }
          break;
        }
        case ActionTypes.DOUBLE: {
          let keys = action.data.keys;
          if (!Array.isArray(keys)) keys = [keys];
          const { nodeItems, deletedItems } = this.state;
          const nodeItemsTmp = nodeItems;
          const deletedItemsTmp = deletedItems;
          for (const key of keys) {
            deletedItemsTmp[key] = nodeItemsTmp[key];
            delete nodeItemsTmp[key];

            this.props.setDeletedId({ key, value: this.props.ids[key] });
            this.props.clearIds({ key });

            this.props.setDeletedType({ key, value: this.props.types[key] });
            this.props.clearTypes({ key });

            this.props.setDeletedTexture({
              key,
              texture: this.props.textures[key],
            });
            this.props.clearTextures({ key });

            this.props.setDeletedScale({ key, value: this.props.scales[key] });
            this.props.clearScales({ key });

            this.props.setDeletedRotation({
              key,
              value: this.props.rotations[key],
            });
            this.props.clearRotations({ key });

            this.deletedOffsets[key] = this.offsets[key];
            delete this.offsets[key];

            deletedItemsTmp[key].visible = false;
          }
          this.setState({
            nodeItems: nodeItemsTmp,
            deletedItems: deletedItemsTmp,
          });
          break;
        }
        case ActionTypes.SET_SIZE: {
          let keys = action.data.keys;
          if (!Array.isArray(keys)) keys = [keys];
          const allAxies: ("x" | "y" | "z")[] = ["x", "y", "z"];
          for (const key of keys) {
            if (!action.data.scale) throw new Error("No scale data");
            for (const axis of allAxies) {
              this.handleScale(axis, action.data.scale.from[axis], key, false);
            }
          }
          break;
        }
        case ActionTypes.GROUP: {
          const groupKey = Array.isArray(action.data.keys)
            ? action.data.keys[0]
            : action.data.keys;
          if (!this.props.groups[groupKey]) throw new Error("Group not found");
          const group = this.props.groups[groupKey];
          const nodeItemsTmp = this.state.nodeItems;
          const deletedItemsTmp = this.state.deletedItems;

          for (const key in group.types) {
            this.props.setType({ key, type: group.types[key] });
            this.props.setId({ key, id: group.ids[key] });
            this.props.setRotation({ key, angle: group.rotations[key] });
            this.props.setScales({ key, scale: group.scales[key] });
            this.props.setTexture({ key, texture: group.textures[key] });
            this.offsets[key] = group.offsets[key];
            nodeItemsTmp[key] = deletedItemsTmp[key];
            delete deletedItemsTmp[key];
          }
          deletedItemsTmp[groupKey] = nodeItemsTmp[groupKey];
          delete nodeItemsTmp[groupKey];
          this.deletedOffsets[groupKey] = this.offsets[groupKey];
          delete this.offsets[groupKey];
          this.props.setDeletedType({
            key: groupKey,
            value: this.props.types[groupKey],
          });
          this.props.clearTypes({ key: groupKey });
          this.props.setDeletedId({
            key: groupKey,
            value: this.props.ids[groupKey],
          });
          this.props.clearIds({ key: groupKey });
          this.props.setDeletedRotation({
            key: groupKey,
            value: this.props.rotations[groupKey],
          });
          this.props.clearRotations({ key: groupKey });
          this.props.setDeletedScale({
            key: groupKey,
            value: this.props.scales[groupKey],
          });
          this.props.clearScales({ key: groupKey });

          this.setState({
            nodeItems: nodeItemsTmp,
            deletedItems: deletedItemsTmp,
          });
          this.props.setDeletedGroup({
            key: groupKey,
            value: this.props.groups[groupKey],
          });
          this.props.clearGroups({ key: groupKey });

          break;
        }
        case ActionTypes.SET_TEXTURE: {
          if (action.data.texture) {
            for (const key of action.data.keys) {
              this.changeTexture(key, action.data.texture.from, false);
            }
          }
          break;
        }
      }
    }
  }

  processRedo(prevIndex: number) {
    for (let index = prevIndex + 1; index <= this.props.actionsIndex; index++) {
      const action = this.props.actionsHistory[index];
      if (!action) continue;
      this.props.clearMovingNodes();
      switch (action.type) {
        case ActionTypes.MIRROR: {
          if (action.data.mirror && action.data.mirror.axis)
            this.handleMirror(action.data.mirror.axis, action.data.keys, false);
          break;
        }
        case ActionTypes.SET_ROTATION: {
          if (action.data.rotate) {
            console.log(action.data.rotate.from - action.data.rotate.to);
            this.handleRotate(
              action.data.rotate.from - action.data.rotate.to,
              action.data.keys,
              false
            );
          }
          break;
        }
        case ActionTypes.SET_OFFSET: {
          if (action.data.offset) {
            this.moveNodes(
              action.data.offset.axis,
              action.data.offset.distance,
              action.data.keys
            );
          }
          break;
        }
        case ActionTypes.ADD_MODEL: {
          if (action.data.model) {
            let keys = action.data.keys;
            if (!Array.isArray(keys)) keys = [keys];
            for (const key of keys) {
              const node = this.state.nodeItems[key];
              node.visible = true;
            }
          }
          break;
        }
        case ActionTypes.REMOVE_MODEL: {
          let keys = action.data.keys;
          if (!Array.isArray(keys)) keys = [keys];
          this.handleDelete(keys, false);
          break;
        }
        case ActionTypes.REPLACE_MODEL: {
          if (Array.isArray(action.data.keys)) {
            const [newKey, oldKey] = action.data.keys;
            const { nodeItems, deletedItems } = this.state;
            const nodeItemsTmp = nodeItems;
            const deletedItemsTmp = deletedItems;

            deletedItemsTmp[oldKey].visible = true;
            nodeItemsTmp[oldKey] = deletedItemsTmp[oldKey];
            delete deletedItemsTmp[oldKey];

            nodeItemsTmp[newKey].visible = false;
            deletedItemsTmp[newKey] = nodeItemsTmp[newKey];
            delete nodeItemsTmp[newKey];

            // newKey -> deleted
            // oldKey -> state

            this.offsets[oldKey] = this.deletedOffsets[oldKey];
            delete this.deletedOffsets[oldKey];
            this.deletedOffsets[newKey] = this.offsets[newKey];
            delete this.offsets[newKey];

            this.props.setId({
              key: oldKey,
              id: this.props.deletedItems.ids[oldKey],
            });
            this.props.setDeletedId({
              key: newKey,
              value: this.props.ids[newKey],
            });
            this.props.clearIds({ key: newKey });
            this.props.clearDeletedIds({ key: oldKey });

            this.props.setType({
              key: oldKey,
              type: this.props.deletedItems.types[oldKey],
            });
            this.props.setDeletedType({
              key: newKey,
              value: this.props.types[newKey],
            });
            this.props.clearTypes({ key: newKey });
            this.props.clearDeletedTypes({ key: oldKey });

            this.props.setTexture({
              key: oldKey,
              texture: this.props.deletedItems.textures[oldKey],
            });
            this.props.setDeletedTexture({
              key: newKey,
              texture: this.props.textures[newKey],
            });
            this.props.clearTextures({ key: newKey });
            this.props.clearDeletedTextures({ key: oldKey });

            this.props.setRotation({
              key: oldKey,
              angle: this.props.deletedItems.rotations[oldKey],
            });
            this.props.setDeletedRotation({
              key: newKey,
              value: this.props.rotations[newKey],
            });
            this.props.clearRotations({ key: newKey });
            this.props.clearDeletedRotations({ key: oldKey });

            this.props.setScales({
              key: oldKey,
              scale: this.props.deletedItems.scales[oldKey],
            });
            this.props.setDeletedScale({
              key: newKey,
              value: this.props.scales[newKey],
            });
            this.props.clearScales({ key: newKey });
            this.props.clearDeletedScales({ key: oldKey });

            this.setState({
              nodeItems: nodeItemsTmp,
              deletedItems: deletedItemsTmp,
            });
          }
          break;
        }
        case ActionTypes.DOUBLE: {
          const { nodeItems, deletedItems } = this.state;
          const nodeItemsTmp = nodeItems;
          const deletedItemsTmp = deletedItems;
          let keys = action.data.keys;
          if (!Array.isArray(keys)) keys = [keys];
          for (const key of keys) {
            nodeItemsTmp[key] = deletedItemsTmp[key];
            delete deletedItemsTmp[key];

            this.offsets[key] = this.deletedOffsets[key];
            delete this.deletedOffsets[key];

            this.props.setId({ key, id: this.props.deletedItems.ids[key] });
            this.props.clearDeletedIds({ key });

            this.props.setType({
              key,
              type: this.props.deletedItems.types[key],
            });
            this.props.clearDeletedTypes({ key });
            this.props.setTexture({
              key,
              texture: this.props.deletedItems.textures[key],
            });
            this.props.clearDeletedTextures({ key });

            this.props.setRotation({
              key,
              angle: this.props.deletedItems.rotations[key],
            });
            this.props.clearDeletedRotations({ key });

            this.props.setScales({
              key,
              scale: this.props.deletedItems.scales[key],
            });
            this.props.clearDeletedScales({ key });
            nodeItemsTmp[key].visible = true;
          }
          this.setState({ nodeItems: nodeItems, deletedItems: deletedItems });
          break;
        }
        case ActionTypes.SET_SIZE: {
          let keys = action.data.keys;
          if (!Array.isArray(keys)) keys = [keys];
          const allAxies: ("x" | "y" | "z")[] = ["x", "y", "z"];
          for (const key of keys) {
            if (!action.data.scale) throw new Error("No scale data");
            for (const axis of allAxies) {
              this.handleScale(axis, action.data.scale.to[axis], key, false);
            }
          }
          break;
        }
        case ActionTypes.GROUP: {
          const groupKey = Array.isArray(action.data.keys)
            ? action.data.keys[0]
            : action.data.keys;
          if (!this.props.deletedItems.groups[groupKey])
            throw new Error("Group not found");
          const nodeItemsTmp = this.state.nodeItems;
          const deletedItemsTmp = this.state.deletedItems;

          if (!action.data.group) throw new Error("No group info");

          for (const key of action.data.group.keys) {
            deletedItemsTmp[key] = nodeItemsTmp[key];
            delete nodeItemsTmp[key];
            this.deletedOffsets[key] = this.offsets[key];
            delete this.offsets[key];

            this.props.setDeletedType({ key, value: this.props.types[key] });
            this.props.clearTypes({ key });
            this.props.setDeletedId({ key, value: this.props.ids[key] });
            this.props.clearIds({ key });
            this.props.setDeletedRotation({
              key,
              value: this.props.rotations[key],
            });
            this.props.clearRotations({ key });
            this.props.setDeletedScale({ key, value: this.props.scales[key] });
            this.props.clearScales({ key });
          }
          nodeItemsTmp[groupKey] = deletedItemsTmp[groupKey];
          delete deletedItemsTmp[groupKey];
          this.offsets[groupKey] = this.deletedOffsets[groupKey];
          delete this.deletedOffsets[groupKey];

          this.props.setId({
            key: groupKey,
            id: this.props.deletedItems.ids[groupKey],
          });
          this.props.clearDeletedIds({ key: groupKey });
          this.props.setType({
            key: groupKey,
            type: this.props.deletedItems.types[groupKey],
          });
          this.props.clearDeletedTypes({ key: groupKey });
          this.props.setRotation({
            key: groupKey,
            angle: this.props.deletedItems.rotations[groupKey],
          });
          this.props.clearDeletedRotations({ key: groupKey });
          this.props.setScales({
            key: groupKey,
            scale: this.props.deletedItems.scales[groupKey],
          });
          this.props.clearDeletedScales({ key: groupKey });
          this.props.setGroup({
            key: groupKey,
            group: this.props.deletedItems.groups[groupKey],
          });
          this.props.clearDeletedGroups({ key: groupKey });
          break;
        }
        case ActionTypes.SET_TEXTURE: {
          if (action.data.texture) {
            for (const key of action.data.keys) {
              this.changeTexture(key, action.data.texture.to, false);
            }
          }
          break;
        }
      }
    }
  }

  async createCovering(args: {
    model: ItemInfo;
    x: number;
    z: number;
    fill: boolean;
    name: string;
  }): Promise<string> {
    const oldKey = await this.loadObjects(
      `${args.model.path3D.dirname}/${args.model.path3D.obj}`,
      `${args.model.path3D.dirname}/${args.model.path3D.mtl}`,
      "element",
      args.model.id
    );
    const node = this.state.nodeItems[oldKey];
    const sizes = new Vector3();
    new Box3().setFromObject(node).getSize(sizes);
    const addedKeys: string[] = [];
    for (var xi = 0; xi < args.x; xi++) {
      let copy = node.clone();
      copy.uuid = MathUtils.generateUUID();
      addedKeys.push(copy.uuid);
      await this.setStateAsync({
        nodeItems: { ...this.state.nodeItems, [copy.uuid]: copy },
      });
      this.props.setId({ key: copy.uuid, id: this.props.ids[oldKey] });
      this.props.setType({ key: copy.uuid, type: this.props.types[oldKey] });
      this.props.setTexture({
        key: copy.uuid,
        texture: this.props.textures[oldKey],
      });
      this.props.setSaveProportions({
        key: copy.uuid,
        value: this.props.saveProportions[oldKey],
      });
      this.props.setScales({ key: copy.uuid, scale: { ...copy.scale } });
      this.props.setRotation({
        key: copy.uuid,
        angle: this.props.rotations[oldKey],
      });

      this.offsets[copy.uuid] = { ...this.offsets[oldKey] };
      copy.position.set(
        this.offsets[oldKey].x - sizes.x * 0.5,
        this.offsets[oldKey].y,
        this.offsets[oldKey].z - sizes.z * 0.5
      );
      copy.position.x += sizes.x * xi;
      this.offsets[copy.uuid].x += sizes.x * xi;
      this.scene.add(copy);
    }
    for (let zi = 1; zi < args.z; zi++) {
      let copy = node.clone();
      copy.uuid = MathUtils.generateUUID();
      addedKeys.push(copy.uuid);

      await this.setStateAsync({
        nodeItems: { ...this.state.nodeItems, [copy.uuid]: copy },
      });
      this.props.setId({ key: copy.uuid, id: this.props.ids[oldKey] });
      this.props.setType({ key: copy.uuid, type: this.props.types[oldKey] });
      this.props.setTexture({
        key: copy.uuid,
        texture: this.props.textures[oldKey],
      });
      this.props.setSaveProportions({
        key: copy.uuid,
        value: this.props.saveProportions[oldKey],
      });
      this.props.setScales({ key: copy.uuid, scale: { ...copy.scale } });
      this.props.setRotation({
        key: copy.uuid,
        angle: this.props.rotations[oldKey],
      });

      this.offsets[copy.uuid] = { ...this.offsets[oldKey] };
      copy.position.set(
        this.offsets[oldKey].x - sizes.x * 0.5,
        this.offsets[oldKey].y,
        this.offsets[oldKey].z - sizes.z * 0.5
      );
      copy.position.z += sizes.z * zi;
      this.offsets[copy.uuid].z += sizes.z * zi;
      this.scene.add(copy);
      if (args.fill) {
        for (let xi = 1; xi < args.x; xi++) {
          let copy = node.clone();
          copy.uuid = MathUtils.generateUUID();
          addedKeys.push(copy.uuid);

          await this.setStateAsync({
            nodeItems: { ...this.state.nodeItems, [copy.uuid]: copy },
          });
          this.props.setId({ key: copy.uuid, id: this.props.ids[oldKey] });
          this.props.setType({
            key: copy.uuid,
            type: this.props.types[oldKey],
          });
          this.props.setTexture({
            key: copy.uuid,
            texture: this.props.textures[oldKey],
          });
          this.props.setSaveProportions({
            key: copy.uuid,
            value: this.props.saveProportions[oldKey],
          });
          this.props.setScales({ key: copy.uuid, scale: { ...copy.scale } });
          this.props.setRotation({
            key: copy.uuid,
            angle: this.props.rotations[oldKey],
          });

          this.offsets[copy.uuid] = { ...this.offsets[oldKey] };
          copy.position.set(
            this.offsets[oldKey].x - sizes.x * 0.5,
            this.offsets[oldKey].y,
            this.offsets[oldKey].z - sizes.z * 0.5
          );
          copy.position.x += sizes.x * xi;
          copy.position.z += sizes.z * zi;
          this.offsets[copy.uuid].x += sizes.x * xi;
          this.offsets[copy.uuid].z += sizes.z * zi;
          this.scene.add(copy);
        }
      } else if (zi === args.z - 1) {
        for (let xi = 1; xi < args.x; xi++) {
          let copy = node.clone();
          copy.uuid = MathUtils.generateUUID();
          addedKeys.push(copy.uuid);

          await this.setStateAsync({
            nodeItems: { ...this.state.nodeItems, [copy.uuid]: copy },
          });
          this.props.setId({ key: copy.uuid, id: this.props.ids[oldKey] });
          this.props.setType({
            key: copy.uuid,
            type: this.props.types[oldKey],
          });
          this.props.setTexture({
            key: copy.uuid,
            texture: this.props.textures[oldKey],
          });
          this.props.setSaveProportions({
            key: copy.uuid,
            value: this.props.saveProportions[oldKey],
          });
          this.props.setScales({ key: copy.uuid, scale: { ...copy.scale } });
          this.props.setRotation({
            key: copy.uuid,
            angle: this.props.rotations[oldKey],
          });

          this.offsets[copy.uuid] = { ...this.offsets[oldKey] };
          copy.position.set(
            this.offsets[oldKey].x - sizes.x * 0.5,
            this.offsets[oldKey].y,
            this.offsets[oldKey].z - sizes.z * 0.5
          );
          copy.position.x += sizes.x * xi;
          copy.position.z += sizes.z * zi;
          this.offsets[copy.uuid].x += sizes.x * xi;
          this.offsets[copy.uuid].z += sizes.z * zi;
          this.scene.add(copy);
        }
        for (let i = 1; i < args.z - 1; i++) {
          let copy = node.clone();
          copy.uuid = MathUtils.generateUUID();
          addedKeys.push(copy.uuid);

          await this.setStateAsync({
            nodeItems: { ...this.state.nodeItems, [copy.uuid]: copy },
          });
          this.props.setId({ key: copy.uuid, id: this.props.ids[oldKey] });
          this.props.setType({
            key: copy.uuid,
            type: this.props.types[oldKey],
          });
          this.props.setTexture({
            key: copy.uuid,
            texture: this.props.textures[oldKey],
          });
          this.props.setSaveProportions({
            key: copy.uuid,
            value: this.props.saveProportions[oldKey],
          });
          this.props.setScales({ key: copy.uuid, scale: { ...copy.scale } });
          this.props.setRotation({
            key: copy.uuid,
            angle: this.props.rotations[oldKey],
          });

          this.offsets[copy.uuid] = { ...this.offsets[oldKey] };
          copy.position.set(
            this.offsets[oldKey].x - sizes.x * 0.5,
            this.offsets[oldKey].y,
            this.offsets[oldKey].z - sizes.z * 0.5
          );
          copy.position.x += sizes.x * (args.x - 1);
          copy.position.z += sizes.z * i;
          this.offsets[copy.uuid].x += sizes.x * (args.x - 1);
          this.offsets[copy.uuid].z += sizes.z * i;
          this.scene.add(copy);
        }
      }
    }
    const groupKey = this.handleGroup(args.name, [...addedKeys, oldKey], false);
    this.props.toggleMovingNode(groupKey);
    this.handleFocus();
    return groupKey;
  }

  async saveProject() {
    if (this.props.canvasRef.current === null)
      throw new Error("Canvas no found");
    const file = await srcToFile(
      this.props.canvasRef.current.toDataURL("image/png"),
      "preview.png",
      "image/png"
    );

    type Project = {
      name?: string;
      types: string[];
      ids: number[];
      offsets: { x: number; y: number; z: number }[];
      scales: { x: number; y: number; z: number }[];
      rotations: number[];
      groups?: Project[];
      textures: string[]
    };

    const project: Project = {
      types: [],
      ids: [],
      offsets: [],
      scales: [],
      rotations: [],
      groups: [],
      textures: []
    };

    for (const key in this.state.nodeItems) {
      project.types.push(this.props.types[key]);
      project.offsets.push(this.offsets[key]);
      project.rotations.push(this.props.rotations[key]);
      project.scales.push(this.state.nodeItems[key].scale);
      
      if(this.props.textures[key]) project.textures.push(this.props.textures[key]);
      else project.textures.push('');

      if (this.props.types[key] !== "group") {
        project.ids.push(this.props.ids[key]);
      } else {
        project.ids.push(0);
        const group: Project = {
          name: "",
          types: [],
          ids: [],
          offsets: [],
          rotations: [],
          scales: [],
          textures: []
        };
        const groupItem = this.props.groups[key];
        group.name = groupItem.name;
        for (const gKey in groupItem.types) {
          group.types.push(groupItem.types[gKey]);
          group.ids.push(groupItem.ids[gKey]);
          group.offsets.push(groupItem.offsets[gKey]);
          group.rotations.push(groupItem.rotations[gKey]);
          group.scales.push(groupItem.scales[gKey]);
          group.textures.push(groupItem.textures[gKey]);
        }
        project.groups?.push(group);
      }
    }
    console.log(project);

    const variables = {
      name: "test",
      ...project,
      preview: file,
      groups: project.groups?.map((group) => JSON.stringify(group)),
      offsets: project.offsets?.map((offset) => JSON.stringify(offset)),
      scales: project.scales?.map((scale) => JSON.stringify(scale)),
      camera: JSON.stringify({
        position: this.camera?.position,
        rotation: {
          x: this.camera?.rotation.x,
          y: this.camera?.rotation.y,
          z: this.camera?.rotation.z,
        },
      }),
    };

    console.log(variables);

    try {
      await this.props.client?.mutate({
        mutation: SAVE_PROJECT,
        variables,
        update: (cache, { data }) => {
          const prData = cache.readQuery<{ projects: ProjectPayload }>({
            query: LOAD_PROJECTS,
            variables: { published: false, favorite: false },
          });
          if (prData && prData.projects) {
            const { projects } = prData;
            let newItems = [data.saveProject, ...projects.items];
            const total = projects.total ? projects.total + 1 : 1;
            cache.writeQuery({
              query: LOAD_PROJECTS,
              variables: { published: false, favorite: false },
              data: {
                projects: {
                  items: newItems,
                  total,
                },
              },
            });
          }
        },
      });
      this.toast({
        position: "top-right",
        status: "success",
        title: "Проект сохранен!",
      });
    } catch (ex) {
      this.toast({
        position: "top-right",
        status: "error",
        title: `Ошибка: ${ex.message}`,
      });
    }
  }

  async init() {
    if (this.state.initialized) return;
    if (this.props.canvasRef == null || !this.props.canvasRef.current) {
      throw new Error("No canvas");
    }
    this.setState({ initialized: true, preloaderVisible: true });

    const canvas = this.props.canvasRef;

    this.renderer = new WebGL1Renderer({
      canvas: canvas.current as HTMLCanvasElement,
      preserveDrawingBuffer: true,
    });
    this.renderer.shadowMap.enabled = true;

    const fov = 45;
    const near = 0.1;
    const far = 100;
    this.camera = new PerspectiveCamera(
      fov,
      (canvas.current as HTMLCanvasElement).width /
        (canvas.current as HTMLCanvasElement).height,
      near,
      far
    );
    this.controls = new OrbitControls(
      this.camera,
      canvas.current as HTMLCanvasElement
    );
    this.controls.target.set(0, 5, 0);
    this.controls.update();

    this.scene.background = new Color("#e0dbcf");
    // const loader = new CubeTextureLoader();
    // const texture = loader.load([
    //   'images/forest-skyboxes/Plants/posx.jpg',
    //   'images/forest-skyboxes/Plants/negx.jpg',
    //   'images/forest-skyboxes/Plants/posy.jpg',
    //   'images/forest-skyboxes/Plants/negy.jpg',
    //   'images/forest-skyboxes/Plants/posz.jpg',
    //   'images/forest-skyboxes/Plants/negz.jpg',
    // ]);
    // this.scene.background = texture;

    const { colors, intensity } = this.state;
    this.light = new HemisphereLight(
      colors.skyColor,
      colors.groundColor,
      intensity.value
    );

    const d = 3000;
    this.spotlight = new SpotLight(0xfdfbd3, 4); //0xffa95c
    this.spotlight.castShadow = true;
    this.spotlight.position.set(0,d,d);
    this.spothelper = new SpotLightHelper(this.spotlight);
    this.spotlight.shadow.bias = -0.000001;
    this.spotlight.shadow.mapSize.width = 1024*4;
    this.spotlight.shadow.mapSize.height = 1024*4;
    this.scene.add(this.light);
    this.scene.add(this.spotlight);
    // this.scene.add(this.spothelper);

    // @ts-ignore
    this.spotlight.shadow.camera.left=-d;
    // @ts-ignore
    this.spotlight.shadow.camera.right=d;
    // @ts-ignore
    this.spotlight.shadow.camera.top=d;
    // @ts-ignore
    this.spotlight.shadow.camera.bottom=-d;

    this.spotlight.shadow.camera.far=19000;
    const helper = new CameraHelper(this.spotlight.shadow.camera);
    // this.scene.add(helper);
    // this.spotlight.shadowDarkness=.2;


    this.setState({ preloaderVisible: false });

    this.start();
  }

  moveNodes(
    axis: string,
    value: number,
    keys: string | string[] = this.props.movingNodes
  ) {
    const { nodeItems } = this.state;
    if (!Array.isArray(keys)) keys = [keys];
    for (const key of keys) {
      if (!nodeItems[key]) continue;
      switch (axis) {
        case "x":
          nodeItems[key].position.x += value;
          this.offsets[key] = {
            ...this.offsets[key],
            x: this.offsets[key].x + value,
          };
          break;
        case "y":
          nodeItems[key].position.y += value;
          this.offsets[key] = {
            ...this.offsets[key],
            y: this.offsets[key].y + value,
          };
          break;
        case "z":
          nodeItems[key].position.z += value;
          this.offsets[key] = {
            ...this.offsets[key],
            z: this.offsets[key].z + value,
          };
          break;
      }
    }
    this.drawHelper();
  }

  async center(element: Object3D) {
    const bb = new Box3().setFromObject(element);
    const dx = -bb.max.x + -(bb.min.x - bb.max.x) / 2;

    // const dy = -bb.max.y + -(bb.min.x - bb.max.y) / 2;
    const dz = -bb.max.z + -(bb.min.z - bb.max.z) / 2;
    element.position.x += dx;
    // element.position.y += dy;
    element.position.z += dz;
    return;
  }

  rotateNodes(
    angle: number,
    keys: string | string[] = this.props.movingNodes,
    native: boolean = true
  ): void {
    const rawAngle = angle;
    angle *= Math.PI / 180;
    if (!Array.isArray(keys)) keys = [keys];
    for (const key of keys) {
      const mesh: Object3D = this.state.nodeItems[key];
      if (!mesh) return;
      mesh.rotateY(angle);
      if (this.offsets[key]) {
        this.center(mesh);
        mesh.position.x += this.offsets[key].x;
        mesh.position.z += this.offsets[key].z;
      }

      const action: Action = {
        type: ActionTypes.SET_ROTATION,
        data: {
          keys: key,
          rotate: {
            from: this.props.rotations[key],
            to: this.props.rotations[key] + rawAngle,
          },
        },
      };

      if (native) this.props.addAction(action);

      this.props.setRotation({
        key,
        angle: this.props.rotations[key] + rawAngle,
      });
      console.log(rawAngle);
      if (Math.abs(rawAngle / 90) % 2 === 1 && this.props.scales[key]) {
        // need to swap X and Z scales in state
        // this.props.setScales({
        //   key,
        //   scale: {
        //     ...this.props.scales[key],
        //     x: this.props.scales[key].z,
        //     z: this.props.scales[key].x,
        //   },
        // });
      }
      if (
        Math.abs(rawAngle / 90) % 2 === 1 &&
        this.props.deletedItems.scales[key]
      ) {
        this.props.setDeletedScale({
          key,
          value: {
            ...this.props.deletedItems.scales[key],
            x: this.props.deletedItems.scales[key].z,
            z: this.props.deletedItems.scales[key].x,
          },
        });
      }
    }
  }

  getSize(node: Object3D, axis: string): number {
    const box = new Box3().setFromObject(node);
    let max, min;
    switch (axis) {
      case "y":
        max = box.max.y;
        min = box.min.y;
        break;
      case "z":
        max = box.max.z;
        min = box.min.z;
        break;
      default:
        max = box.max.x;
        min = box.min.x;
        break;
    }
    return Math.abs(max - min);
  }

  setCamera(node: Object3D): void {
    const box = new Box3().setFromObject(node);
    const boxSize = box.getSize(new Vector3()).length();
    const boxCenter = box.getCenter(new Vector3());
    this.frameArea(boxSize * 2.2, boxSize, boxCenter, 1);
    if (!this.controls) throw new Error("Controls not initialized");
    this.controls.maxDistance = boxSize * 10;
    if (this.state.nodeItems.fence) {
      const sizeX = this.getSize(this.state.nodeItems.fence, "x");
      this.camera
        ?.translateX(-sizeX / 2 + 500)
        .translateZ(500)
        .translateY(this.camera.position.y * 2);
    }
    this.controls.target.copy(boxCenter);
    this.controls.update();
  }

  handleCenter() {
    for (const key of this.props.movingNodes)
      this.center(this.state.nodeItems[key]);
  }

  start() {
    this.frame = requestAnimationFrame(this.render3d.bind(this));
  }

  render3d() {
    if (
      this.renderer === null ||
      this.scene === null ||
      this.camera === null ||
      this.light === null
    )
      return;

    if (this.resizeRendererToDisplaySize()) {
      const canvas = this.renderer.domElement;
      this.camera.aspect = canvas.clientWidth / canvas.clientHeight;
      this.camera.updateProjectionMatrix();
    }
    const { intensity, colors, nodeItems } = this.state;
    if (intensity.changed) {
      // this.scene.remove(this.light);
      // this.light = new HemisphereLight(
      //   colors.skyColor,
      //   colors.groundColor,
      //   intensity.value
      // );
      // this.scene.add(this.light);
      intensity.changed = false;
      if(this.spotlight) this.spotlight.intensity = intensity.value;
    }
    if (Object.keys(nodeItems).length > 0) {
      Object.keys(nodeItems).forEach((key: string) => {
        let color = 0x000000;
        if (
          this.props.movingNodes.indexOf(key) !== -1 ||
          key === this.props.hoverNode
        )
          color = 0x888888;
        this.setColorRecursive(nodeItems[key], color);
      });
    }
    this.renderer.render(this.scene, this.camera);
    this.frame = requestAnimationFrame(this.render3d.bind(this));
    const dir = new Vector3();
    const sph = new Spherical();

    this.camera.getWorldDirection(dir);
    sph.setFromVector3(dir);
    if (this.props.compassRef.current) {
      this.props.compassRef.current.style.transform = `rotate(${
        MathUtils.radToDeg(sph.theta) - 180
      }deg)`;
    }
  }

  setColorRecursive(node: Object3D, color: number) {
    if (!node.children || node.children.length === 0) {
      const mats = (node as Mesh).material as MeshPhongMaterial;
      if (mats && mats.emissive) mats.emissive = new Color(color);
      return;
    }
    node.children.forEach((child: Object3D) => {
      this.setColorRecursive(child, color);
    });
  }

  frameArea(
    sizeToFitOnScreen: number,
    boxSize: number,
    boxCenter: Vector3,
    koef = 1
  ) {
    if (this.camera == null) return;
    if (Object.keys(this.state.nodeItems).length > 1) return;
    const halfSizeToFitOnScreen = sizeToFitOnScreen * 0.5;
    const halfFovY = MathUtils.degToRad(this.camera.fov * 0.5);
    const distance = halfSizeToFitOnScreen / Math.tan(halfFovY);

    const direction = new Vector3()
      .subVectors(this.camera.position, boxCenter)
      .multiply(new Vector3(0, 0, koef * 3))
      .normalize();

    this.camera.position.copy(
      direction.multiplyScalar(distance).add(boxCenter)
    );

    this.camera.near = boxSize / 100;
    this.camera.far = boxSize * 100;

    this.camera.updateProjectionMatrix();
    this.camera.lookAt(boxCenter.x, boxCenter.y, boxCenter.z);
  }

  resizeRendererToDisplaySize() {
    if (this.renderer === null) return;
    const canvas = this.renderer.domElement;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const needResize = canvas.width !== width || canvas.height !== height;
    if (needResize) {
      this.renderer.setSize(width, height, false);
    }
    return needResize;
  }

  async addGroup(id: number): Promise<void> {
    if (!this.props.client) throw new Error("No Apollo client");
    this.props.setLoading(true);
    this.props.setModalOpen({ key: ModalsName.ADD, value: false });
    const { data, error } = await this.props.client.query({
      query: LOAD_GROUP,
      variables: { id },
    });

    if (error) throw error;

    const group: {
      name: string;
      types: { [key: string]: string };
      ids: { [key: string]: number };
      offsets: { [key: string]: { x: 0; y: 0; z: 0 } };
      rotations: { [key: string]: number };
      models: { [key: string]: any };
    } = {
      name: "asd",
      types: {},
      ids: {},
      offsets: {},
      rotations: {},
      models: {},
    };

    for (let i = 0; i < data.group.types.length; i++) {
      const key = MathUtils.generateUUID();
      group.types[key] = data.group.types[i];
      group.ids[key] = data.group.ids[i];
      group.offsets[key] = data.group.offsets[i];
      group.rotations[key] = data.group.rotations[i];
      group.models[key] = data.group.models[i];
    }

    for (const key in group.models) {
      console.log(key, group.models[key]);
      const path = group.models[key].path3D;
      group.models[key] = {
        ...group.models[key],
        path3D: {
          obj: `${path.dirname}/${path.obj}`,
          mtl: `${path.dirname}/${path.mtl}`,
        },
      };
    }

    console.log(group);
    await this.processGroup(group);
    this.props.setLoading(false);
    return;
  }

  handleMove(arrow: string, coef: number = 1) {
    let axis = "x",
      val = 1;
    if (arrow === "left") {
      axis = "x";
      val *= -1;
    }
    if (arrow === "right") axis = "x";
    if (arrow === "up") {
      axis = "z";
      val *= -1;
    }
    if (arrow === "down") axis = "z";
    if (arrow === "top") axis = "y";
    if (arrow === "bottom") {
      axis = "y";
      val *= -1;
    }

    val *= coef;
    this.moveNodes(axis, val);
  }

  handleFocus() {
    this.setCamera(this.state.nodeItems[this.props.movingNodes[0]]);
  }

  handleRotate(
    angle: number,
    keys: string | string[] = this.props.movingNodes,
    native: boolean = true
  ) {
    console.log("rotate", angle, keys, native);
    this.rotateNodes(angle, keys, native);
    this.processObjectInfo();
  }

  replaceModel = async (
    obj: string,
    mtl: string,
    category: string,
    id: number
  ): Promise<string> => {
    const oldKey = this.props.movingNodes[0];

    const node = this.state.nodeItems[oldKey];
    if (!node) throw new Error("No node to replace");
    if (!this.scene) throw new Error("Scene not initialized yet");

    this.props.toggleMovingNode(oldKey);

    this.props.setDeletedId({ key: oldKey, value: this.props.ids[oldKey] });
    this.props.clearIds({ key: oldKey });

    this.props.setDeletedType({ key: oldKey, value: this.props.types[oldKey] });
    this.props.clearTypes({ key: oldKey });

    this.props.setDeletedTexture({
      key: oldKey,
      texture: this.props.textures[oldKey],
    });
    this.props.clearTextures({ key: oldKey });

    this.props.clearSaveProportions({ key: oldKey });

    const key = await this.loadObjects(obj, mtl, category, id);

    const { nodeItems, deletedItems } = this.state;
    const nodeItemsTmp = nodeItems;
    const deletedItemsTmp = deletedItems;

    deletedItemsTmp[oldKey] = nodeItemsTmp[oldKey];
    delete nodeItemsTmp[oldKey];

    const angle = (this.props.rotations[oldKey] * Math.PI) / 180;
    const mesh: Object3D = this.state.nodeItems[key];
    if (!mesh) throw new Error("No mesh to rotate");
    mesh.rotateY(angle);
    this.center(mesh);

    this.props.setRotation({ key, angle: this.props.rotations[oldKey] });
    this.props.setDeletedRotation({
      key: oldKey,
      value: this.props.rotations[oldKey],
    });
    this.props.clearRotations({ key: oldKey });

    this.deletedOffsets[oldKey] = this.offsets[oldKey];
    this.offsets[key] = { ...this.offsets[oldKey] };
    nodeItemsTmp[key].position.x += this.offsets[key].x;
    nodeItemsTmp[key].position.y += this.offsets[key].y;
    nodeItemsTmp[key].position.z += this.offsets[key].z;

    // this.state.nodeItems[key].scale.set(
    //   this.props.scales[oldKey].x,
    //   this.props.scales[oldKey].y,
    //   this.props.scales[oldKey].z
    // );
    this.props.setScales({ key, scale: this.props.scales[oldKey] });
    this.props.setDeletedScale({
      key: oldKey,
      value: this.props.scales[oldKey],
    });
    this.props.clearScales({ key: oldKey });

    delete nodeItemsTmp[oldKey];
    delete this.offsets[oldKey];
    deletedItemsTmp[oldKey].visible = false;
    this.setState({ nodeItems: nodeItemsTmp, deletedItems: deletedItemsTmp });
    this.props.setMovingNode({ index: 0, key });
    this.props.addAction({
      type: ActionTypes.REPLACE_MODEL,
      data: {
        keys: [oldKey, key],
      },
    });
    return key;
  };

  loadMtlAsync(url: string): Promise<MTLLoader.MaterialCreator> {
    return new Promise((resolve, reject) => {
      let managerMTL = new LoadingManager();
      let mtlLoader = new MTLLoader(managerMTL);
      mtlLoader.setMaterialOptions({
        side: DoubleSide,
      });
      mtlLoader.load(
        url,
        (mats: MTLLoader.MaterialCreator) => resolve(mats),
        () => undefined,
        (err: any) => {
          console.log(err);
          reject(err);
        }
      );
    });
  }

  loadObjAsync(
    url: string,
    materials: MTLLoader.MaterialCreator
  ): Promise<Group> {
    return new Promise((resolve, reject) => {
      let managerOBJ = new LoadingManager();
      let objLoader = new OBJLoader(managerOBJ);
      try {
        objLoader.setMaterials(materials);
      } catch (ex) {
        reject(ex);
      }
      objLoader.load(url, resolve, () => undefined, reject);
    });
  }

  testUrl(url: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      fetch(url)
        .then((res) => {
          if (res.status === 200) resolve(true);
          else resolve(false);
        })
        .catch(() => {
          resolve(false);
        });
    });
  }

  loadTextureAsync(url: string): Promise<Texture> {
    return new Promise((resolve, reject) => {
      new TextureLoader().load(
        url,
        (texture: Texture) => resolve(texture),
        undefined,
        (err: ErrorEvent) => {
          console.log(err);
          reject(err);
        }
      );
    });
  }

  async changeTexture(
    keys: string | string[] = this.props.movingNodes,
    src: string = "",
    native: boolean = true
  ) {
    if (src === "") return;
    this.props.setLoading(true);
    if (!Array.isArray(keys)) keys = [keys];
    const texture = await this.loadTextureAsync(src);
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    for (const key of keys) {
      console.log(key);
      const node = this.state.nodeItems[key];
      if (!node) continue;

      let txt = '';      

      for (const child of node.children) {
        const mesh = child as Mesh;
        const material = mesh.material as MeshPhongMaterial;
        if (!!material) {
          if(txt === '') txt = material.map?.image.src;
          material.map = texture;
          material.needsUpdate = true;
        }
      }

      if (native)
        this.props.addAction({
          type: ActionTypes.SET_TEXTURE,
          data: {
            keys: [key],
            texture: {
              from: txt ? txt.replace (/^[a-z]{4,5}\:\/{2}[a-z]{1,}\:[0-9]{1,4}.(.*)/, '$1') : '',
              to: src,
            },
          },
        });
    }
    this.props.setLoading(false);
  }

  debounce(func: Function): (this: HTMLCanvasElement, ev: MouseEvent) => void {
    var timer: number;
    return (event: Event) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(func, 100, event);
    };
  }

  async loadObjects(
    objUrl: string,
    mtlUrl: string,
    category: string = "",
    id: number
  ): Promise<string> {
    if (!objUrl) throw new Error("No obj url");

    // this.props.setLoading(true);
    this.props.setLoading(true);
    let materials;
    try {
      materials = await this.loadMtlAsync(mtlUrl);
      // materials.preload();
      // Проверяем на наличие данных текстур
      const base = materials.baseUrl;
      let hasBadTexture = false;
      for (const txtName in materials.materialsInfo) {
        let textureOk = true;
        if (materials.materialsInfo[txtName].map_kd)
          textureOk = await this.testUrl(
            base + materials.materialsInfo[txtName].map_kd?.replace(/\\/gi, "/")
          );
        if (!textureOk) {
          hasBadTexture = true;
          break;
        }
      }
      if (hasBadTexture) {
        console.log("Bad texture");
        for (const key in materials.materials) {
          if (materials.materialsInfo[key].map_kd) {
            let mesh = materials.materials[key];
            let src = `/ymaxiProduct/billing2/php/modules/onlineEditor/images/3d/commonTextures/Granite_fixed.jpg`;
            new TextureLoader().load(
              src,
              (texture) => {
                texture.wrapS = RepeatWrapping;
                texture.wrapT = RepeatWrapping;
                // @ts-ignore
                mesh.map = texture;
                mesh.needsUpdate = true;
              },
              (xhr) => {
                console.log(xhr.loaded, xhr.total, xhr);
              },
              (err) => {
                console.error("fuck", err);
              }
            );
          }
        }
      }
    } catch (ex) {
      console.log("cannot load mats");
      throw ex;
    }
    const root = await this.loadObjAsync(objUrl, materials);
    if (this.scene === null) throw new Error("Scene not initialized");
    if (this.controls === null) throw new Error("Controls not initialized");

    root.traverse((n:Object3D) => {
      if((n as Mesh).isMesh) {
        n.castShadow = true; 
        n.receiveShadow = true;
        const mat = ((n as Mesh).material as MeshPhongMaterial);
        if(mat.map) mat.map.anisotropy = 16; 
      }
    })

    this.scene.add(root);
    this.offsets[root.uuid] = { x: 0, y: 0, z: 0 };
    await this.setStateAsync({
      nodeItems: { ...this.state.nodeItems, [root.uuid]: root },
    });

    this.props.setType({ key: root.uuid, type: category });
    this.props.setSaveProportions({ key: root.uuid, value: false });
    this.props.setScales({ key: root.uuid, scale: { ...root.scale } });
    this.props.setId({ key: root.uuid, id });
    this.props.setRotation({ key: root.uuid, angle: 0 });
    this.props.setTexture({ key: root.uuid, texture: "" });

    if (id < 0) {
      // sketch
      if (!this.props.client) throw new Error("No Apollo client");
      const { data, error } = await this.props.client.query({
        query: gql`
          query LoadSketch($id: Int!) {
            monument(id: $id) {
              id
              sketchUrl
              sketchUrl2
              workspaces
            }
          }
        `,
        variables: { id },
      });

      if (error)
        this.toast({
          title: `Ошибка: ${error.message}`,
          position: "top-right",
          variant: "left-accent",
          status: "error",
        });
      else {
        // Накладываем эскизы
        await this.loadSketch(
          `/api/download/${data.monument.sketchUrl}`,
          `/api/download/${data.monument.sketchUrl2}`,
          root,
          data.monument.workspaces
        );
      }
    }

    this.center(root);

    let maxS = 0,
      sIndex = 0;
    root.children.forEach(function (child, index) {
      var box = new Box3().setFromObject(child);
      var sizes = box.getSize(new Vector3(1, 1, 1));
      var square = sizes.x * sizes.y;
      if (square > maxS) {
        maxS = square;
        sIndex = index;
      }
    });
    var mainBox = new Box3().setFromObject(root.children[sIndex]);
    let cameraCoef =
      Math.abs(mainBox.max.z) > 0 && Math.abs(mainBox.min.z) > 0 ? 1 : -1;

    const box = new Box3().setFromObject(root);
    const boxSize = box.getSize(new Vector3()).length();
    const boxCenter = box.getCenter(new Vector3());
    // console.log(boxSize, boxCenter);
    this.frameArea(boxSize * 1.2, boxSize, boxCenter, cameraCoef);
    // this.controls.maxDistance = boxSize * 10;
    // this.controls.target.copy(boxCenter);
    // this.controls.update();
    // this.start();
    this.props.setLoading(false);

    return root.uuid;
  }

  async loadSketch(
    sketchUrl: string,
    sketchUrl2: string | null = null,
    node: any,
    workspaces: string[] = []
  ) {
    if (!!sketchUrl) {
      let imgWidth = 0,
        imgHeight = 0,
        xCenter = 0,
        yCenter = 0,
        zCenter = 0,
        zCenter2 = 0;

      console.log(workspaces);
      if (workspaces.length > 0) {
        let minX = Number.MAX_SAFE_INTEGER,
          maxX = Number.MIN_SAFE_INTEGER,
          minY = Number.MAX_SAFE_INTEGER,
          maxY = Number.MIN_SAFE_INTEGER,
          minZ = Number.MAX_SAFE_INTEGER,
          maxZ = Number.MIN_SAFE_INTEGER;

        node.children.forEach((child: Object3D, index: number) => {
          if (workspaces.indexOf(child.name) !== -1) {
            let box = new Box3().setFromObject(child);
            if (box.min.x < minX) minX = box.min.x;
            if (box.max.x > maxX) maxX = box.max.x;
            if (box.min.y < minY) minY = box.min.y;
            if (box.max.y > maxY) maxY = box.max.y;
            if (box.min.z < minZ) minZ = box.min.z;
            if (box.max.z > maxZ) maxZ = box.max.z;
          }
        });

        imgWidth = maxX - minX;
        imgHeight = maxY - minY;
        xCenter = maxX - imgWidth / 2;
        yCenter = maxY - imgHeight / 2;
        zCenter = maxZ + 1;
        zCenter2 = minZ - 1;
      } else {
        let maxS = 0,
          sIndex = -1;
        node.children.forEach((child: Mesh, index: number) => {
          var box = new Box3().setFromObject(child);
          var sizes = box.getSize(new Vector3(1, 1, 1));
          var square = sizes.x * sizes.y;
          if (square > maxS) {
            maxS = square;
            sIndex = index;
          }
        });

        var mainBox = new Box3().setFromObject(node.children[sIndex]);
        console.log(mainBox);
        let sizes = mainBox.getSize(new Vector3(1, 1, 1));
        console.log(sizes);
        imgWidth = sizes.x;
        imgHeight = sizes.y;
        xCenter = mainBox.max.x - sizes.x / 2;
        yCenter = mainBox.max.y - sizes.y / 2;
        zCenter = mainBox.max.z + 1;
        zCenter2 = mainBox.min.z - 1;
      }

      console.log(
        sketchUrl,
        sketchUrl2,
        node.position,
        xCenter,
        yCenter,
        zCenter,
        zCenter2
      );
      // Добавляем эскиз
      if (sketchUrl2 === null || sketchUrl2.indexOf("null") !== -1) {
        console.log(sketchUrl);
        const texture = await this.loadTextureAsync(sketchUrl);
        const img = new MeshPhongMaterial({ map: texture });
        img.transparent = true;
        if (!img.map) throw new Error("Texture is null");
        img.map.needsUpdate = true;
        img.side = DoubleSide;

        const planeGeo = new PlaneGeometry(imgWidth, imgHeight);
        const sketch = new Mesh(planeGeo, img);

        sketch.position.set(xCenter, yCenter, zCenter);
        node.add(sketch);
      } else {
        const texture1 = new TextureLoader().load(sketchUrl);
        const img = new MeshPhongMaterial({ map: texture1 });
        img.transparent = true;
        if (!img.map) throw new Error("Texture is null");
        img.map.needsUpdate = true;
        img.side = DoubleSide;

        const planeGeo = new PlaneGeometry(imgWidth, imgHeight);
        const plane = new Mesh(planeGeo, img);

        plane.position.set(xCenter, yCenter, zCenter);
        node.add(plane);

        const texture2 = new TextureLoader().load(sketchUrl2);
        texture2.wrapS = RepeatWrapping;
        texture2.repeat.x = -1;
        const img2 = new MeshPhongMaterial({ map: texture2 });
        img2.transparent = true;
        if (!img2.map) throw new Error("Texture2 is null");
        img2.map.needsUpdate = true;
        img2.side = DoubleSide;

        const planeGeo2 = new PlaneGeometry(imgWidth, imgHeight);
        const plane2 = new Mesh(planeGeo2, img2);

        plane2.position.set(xCenter, yCenter, zCenter2);
        node.add(plane2);
      }
    }
  }

  drawHelper() {
    if (this.props.movingNodes.length > 0) {
      this.clearLines();
      const center = new Vector3();
      if (this.props.movingNodes.length > 1) {
        const key = this.props.movingNodes[0];
        let node = this.state.nodeItems[key];
        const box = new Box3().setFromObject(node);
        for (var i = 1; i < this.props.movingNodes.length; i++) {
          node = this.state.nodeItems[this.props.movingNodes[i]];
          box.union(new Box3().setFromObject(node));
        }

        box.getCenter(center);
      } else {
        const node = this.state.nodeItems[this.props.movingNodes[0]];
        const box = new Box3().setFromObject(node);
        box.getCenter(center);
      }

      const xPoints = [];
      const step = 2000;
      const centerX = new Vector3().copy(center);
      centerX.add(new Vector3(step, 0, 0));
      xPoints.push(new Vector3().copy(centerX));
      centerX.sub(new Vector3(step, 0, 0));
      xPoints.push(new Vector3().copy(centerX));
      centerX.sub(new Vector3(step, 0, 0));
      xPoints.push(new Vector3().copy(centerX));
      const materialX = new LineBasicMaterial({ color: 0x0000ff });
      const geometryX = new BufferGeometry().setFromPoints(xPoints);
      const lineX = new Line(geometryX, materialX);
      this.lines = [lineX];
      this.scene.add(lineX);

      const yPoints = [];
      const centerY = new Vector3().copy(center);
      centerY.add(new Vector3(0, step, 0));
      yPoints.push(new Vector3().copy(centerY));
      centerY.sub(new Vector3(0, step, 0));
      yPoints.push(new Vector3().copy(centerY));
      const materialY = new LineBasicMaterial({ color: 0x26bf01 });
      const geometryY = new BufferGeometry().setFromPoints(yPoints);
      const lineY = new Line(geometryY, materialY);
      this.lines.push(lineY);
      this.scene.add(lineY);

      const zPoints = [];
      const centerZ = new Vector3().copy(center);
      centerZ.add(new Vector3(0, 0, step));
      zPoints.push(new Vector3().copy(centerZ));
      centerZ.sub(new Vector3(0, 0, step));
      zPoints.push(new Vector3().copy(centerZ));
      centerZ.sub(new Vector3(0, 0, step));
      zPoints.push(new Vector3().copy(centerZ));
      const materialZ = new LineBasicMaterial({ color: 0xff0000 });
      const geometryZ = new BufferGeometry().setFromPoints(zPoints);
      const lineZ = new Line(geometryZ, materialZ);
      this.lines.push(lineZ);
      this.scene.add(lineZ);
    } else {
      for (const line of this.lines) {
        this.scene.remove(line);
      }
      this.lines = [];
    }
  }

  clearLines() {
    for (const line of this.lines) {
      this.scene.remove(line);
    }
    this.lines = [];
  }

  handleDelete(
    keys: string | string[] = this.props.movingNodes,
    native: boolean = true
  ) {
    if (this.scene === null) return;
    console.log("handleDelete");
    const { nodeItems, deletedItems } = this.state;
    const nodeItemsTmp = nodeItems;
    const deletedItemsTmp = deletedItems;

    if (!Array.isArray(keys)) keys = [keys];
    for (const key of keys) {
      deletedItemsTmp[key] = nodeItemsTmp[key];
      delete nodeItemsTmp[key];

      this.deletedOffsets[key] = this.offsets[key];
      delete this.offsets[key];

      this.props.setDeletedId({ key, value: this.props.ids[key] });
      this.props.clearIds({ key });

      this.props.setDeletedType({ key, value: this.props.types[key] });
      this.props.clearTypes({ key });

      this.props.setDeletedTexture({ key, texture: this.props.textures[key] });
      this.props.clearTextures({ key });

      this.props.setDeletedRotation({ key, value: this.props.rotations[key] });
      this.props.clearRotations({ key });

      this.props.setDeletedScale({ key, value: this.props.scales[key] });
      this.props.clearScales({ key });
      deletedItemsTmp[key].visible = false;
    }

    this.setState({ nodeItems: nodeItemsTmp, deletedItems: deletedItemsTmp });
    if (native) {
      this.props.addAction({
        type: ActionTypes.REMOVE_MODEL,
        data: { keys: this.props.movingNodes },
      });
      this.props.clearMovingNodes();
    }
  }

  async duplicate() {
    const addedKeys: string[] = [];
    for (const oldKey of this.props.movingNodes) {
      const node = this.state.nodeItems[oldKey];
      let copy = node.clone();
      copy.uuid = MathUtils.generateUUID();
      addedKeys.push(copy.uuid);

      const props = this.props;
      await this.setStateAsync({
        nodeItems: { ...this.state.nodeItems, [copy.uuid]: copy },
      });

      props.setId({ key: copy.uuid, id: props.ids[oldKey] });
      props.setType({ key: copy.uuid, type: props.types[oldKey] });
      props.setTexture({ key: copy.uuid, texture: props.textures[oldKey] });
      props.setSaveProportions({
        key: copy.uuid,
        value: props.saveProportions[oldKey],
      });
      props.setScales({ key: copy.uuid, scale: { ...copy.scale } });
      props.setRotation({ key: copy.uuid, angle: props.rotations[oldKey] });

      this.offsets[copy.uuid] = this.offsets[oldKey];
      if (props.types[oldKey] === "group") {
        props.setGroup({ key: copy.uuid, group: props.groups[oldKey] });
      }
      console.log(copy);
      this.scene.add(copy);
      // this.center(copy);
    }
    this.props.clearMovingNodes();
    this.props.setMovingNode({ key: addedKeys });
    this.props.addAction({
      type: ActionTypes.DOUBLE,
      data: {
        keys: addedKeys,
      },
    });
    return;
  }

  clearScene() {
    for (const key in this.state.nodeItems) {
      delete this.offsets[key];
      this.scene.remove(this.state.nodeItems[key]);
    }
    this.props.clearMovingNodes();
    this.props.clearRotations({});
    this.props.clearIds({});
    this.props.clearTypes({});
    this.props.clearGroups({});
    this.props.clearScales({});

    this.deletedOffsets = {};
    this.props.clearActionsHistory({});
    this.setState({ nodeItems: {}, deletedItems: {} });
  }

  handleGroup(
    name: string,
    keys: string[] = this.props.movingNodes,
    native: boolean = true
  ): string {
    const groupState: GroupState = {
      name,
      types: {},
      ids: {},
      rotations: {},
      offsets: {},
      scales: {},
      textures: {},
    };

    const newGroup = new Group();

    const parts: Object3D[] = [];

    const xArr: number[] = [];
    const yArr: number[] = [];
    const zArr: number[] = [];

    for (const key of keys) {
      if (this.state.nodeItems[key]) parts.push(this.state.nodeItems[key]);
      if (this.offsets[key]) {
        groupState.offsets[key] = this.offsets[key];
        xArr.push(groupState.offsets[key].x);
        yArr.push(groupState.offsets[key].y);
        zArr.push(groupState.offsets[key].z);
        delete this.offsets[key];
      }
      if (this.props.ids[key]) {
        groupState.ids[key] = this.props.ids[key];
        this.props.clearIds({ key });
      }
      if (this.props.types[key]) {
        groupState.types[key] = this.props.types[key];
        this.props.clearTypes({ key });
      }
      if (this.props.scales[key]) {
        groupState.scales[key] = this.props.scales[key];
        this.props.clearScales({ key });
      }
      if (this.props.textures[key]) {
        groupState.textures[key] = this.props.textures[key];
        this.props.clearTextures({ key });
      }
      if (Object.keys(this.props.rotations).indexOf(key) !== -1) {
        groupState.rotations[key] = this.props.rotations[key];
        this.props.clearRotations({ key });
      }
    }

    const newNodes: { [key: string]: Object3D } = {};
    const deletedNodeItems = this.state.deletedItems;
    for (const key in this.state.nodeItems) {
      if (keys.indexOf(key) === -1) newNodes[key] = this.state.nodeItems[key];
      else deletedNodeItems[key] = this.state.nodeItems[key];
    }

    this.props.clearMovingNodes();
    this.props.setType({ key: newGroup.uuid, type: "group" });
    this.props.setSaveProportions({ key: newGroup.uuid, value: false });
    this.props.setScales({ key: newGroup.uuid, scale: { x: 1, y: 1, z: 1 } });
    this.props.setGroup({ key: newGroup.uuid, group: groupState });

    // Берем сдвиг как средний сдвиг по всем элементам (сдвиг центра группы)
    this.offsets[newGroup.uuid] = {
      x: (Math.max(...xArr) + Math.min(...xArr)) / 2,
      y: (Math.max(...yArr) + Math.min(...yArr)) / 2,
      z: (Math.max(...zArr) + Math.min(...zArr)) / 2,
    };
    this.props.setRotation({ key: newGroup.uuid, angle: 0 });

    for (const model of parts) newGroup.add(model);

    this.setState({
      nodeItems: { ...newNodes, [newGroup.uuid]: newGroup },
      deletedItems: deletedNodeItems,
    });

    this.scene.add(newGroup);

    if (native) {
      this.props.addAction({
        type: ActionTypes.GROUP,
        data: {
          keys: newGroup.uuid,
          group: {
            keys,
            name,
          },
        },
      });
    }
    return newGroup.uuid;
  }

  handleScale(axis: string, val: number, key: string, native: boolean = true) {
    console.log("Scale", axis, val, key, this.state.nodeItems[key].scale);
    let node = this.state.nodeItems[key];
    let scale: {
      from: { x: number; y: number; z: number };
      to: { x: number; y: number; z: number };
    } = {
      from: { x: node.scale.x, y: node.scale.y, z: node.scale.z },
      to: { x: 1, y: 1, z: 1 },
    };

    if (this.props.saveProportions[key]) {
      scale = {
        from: { x: node.scale.x, y: node.scale.y, z: node.scale.z },
        to: { x: val, y: val, z: val },
      };
      node.scale.set(val, val, val);
      this.processObjectInfo();
    } else {
      switch (axis) {
        case "y":
          if (node.scale.y < 0) val *= -1;
          scale.to = { x: node.scale.x, y: val, z: node.scale.z };
          node.scale.y = val;
          break;
        case "x":
          if (node.scale.x < 0) val *= -1;
          scale.to = { x: node.scale.x, y: node.scale.y, z: node.scale.z };
          scale.to.x = val;
          node.scale.x = val;
          break;
        case "z":
          if (node.scale.z < 0) val *= -1;
          scale.to = { x: node.scale.x, y: node.scale.y, z: node.scale.z };
          scale.to.z = val;
          node.scale.z = val;
          break;
      }
    }

    if (native)
      this.props.addAction({
        type: ActionTypes.SET_SIZE,
        data: {
          keys: key,
          scale,
        },
      });
    if (this.offsets[key] && node) {
      this.center(node);
      node.position.x += this.offsets[key].x;
      node.position.y += this.offsets[key].y;
      node.position.z += this.offsets[key].z;
    }
    setTimeout(() => this.processObjectInfo(), 1000);
  }

  handleMirror(
    axis: "x" | "y" | "z",
    keys: string | string[] = this.props.movingNodes,
    native: boolean = true
  ) {
    if (!Array.isArray(keys)) keys = [keys];
    switch (axis) {
      case "x":
        for (const key of keys) {
          const node = this.state.nodeItems[key];
          node.scale.x *= -1;

          if (this.offsets[key] && node) {
            console.log("mirror");
            this.center(node);
            node.position.x += this.offsets[key].x;
            node.position.y += this.offsets[key].y;
            node.position.z += this.offsets[key].z;
          }
        }
        break;
      case "y":
        for (const key of keys) {
          const node = this.state.nodeItems[key];
          node.scale.y *= -1;

          if (this.offsets[key] && node) {
            console.log("mirror");
            this.center(node);
            node.position.x += this.offsets[key].x;
            node.position.y += this.offsets[key].y;
            node.position.z += this.offsets[key].z;
          }
        }
        break;
      case "z":
        for (const key of keys) {
          const node = this.state.nodeItems[key];
          node.scale.z *= -1;

          if (this.offsets[key] && node) {
            this.center(node);
            node.position.x += this.offsets[key].x;
            node.position.y += this.offsets[key].y;
            node.position.z += this.offsets[key].z;
          }
        }
        break;
    }
    if (native) {
      this.props.addAction({
        type: ActionTypes.MIRROR,
        data: {
          keys: this.props.movingNodes,
          mirror: { axis },
        },
      });
    }
    this.drawHelper();
  }

  render() {
    const { loading, nodeItems, preloaderVisible } = this.state;
    const { canvasRef } = this.props;
    return (
      <Box width="63%" ref={this.containerRef} h="calc(100vh - 36px)">
        <Heading size="md" variant="editor" textAlign="center">
          Онлайн конструктор
        </Heading>
        <Box pos="relative">
          {(loading || this.props.loading) && <LoaderFullwidth />}
          {this.props.movingNodes.length > 0 && false && (
            <VStack>
              <HStack>
                <Text>
                  [State] Offsets - X:
                  {this.offsets[this.props.movingNodes[0]].x}&nbsp;&nbsp;Y:
                  {this.offsets[this.props.movingNodes[0]].y}&nbsp;&nbsp;Z:
                  {this.offsets[this.props.movingNodes[0]].z}
                </Text>
                <Text>
                  Scales - X:{this.props.scales[this.props.movingNodes[0]].x}
                  &nbsp;&nbsp;Y:{this.props.scales[this.props.movingNodes[0]].y}
                  &nbsp;&nbsp;Z:{this.props.scales[this.props.movingNodes[0]].z}
                </Text>
              </HStack>
              <HStack>
                <Text>
                  [Real] Offsets - X:
                  {this.state.nodeItems[this.props.movingNodes[0]].position.x}
                  &nbsp;&nbsp;Y:
                  {this.state.nodeItems[this.props.movingNodes[0]].position.y}
                  &nbsp;&nbsp;Z:
                  {this.state.nodeItems[this.props.movingNodes[0]].position.z}
                </Text>
                <Text>
                  Scales - X:
                  {this.state.nodeItems[this.props.movingNodes[0]].scale.x}
                  &nbsp;&nbsp;Y:
                  {this.state.nodeItems[this.props.movingNodes[0]].scale.y}
                  &nbsp;&nbsp;Z:
                  {this.state.nodeItems[this.props.movingNodes[0]].scale.z}
                </Text>
              </HStack>
            </VStack>
          )}
          <Box
            ref={this.props.compassRef}
            pos="absolute"
            left={0}
            top={0}
            w="130px"
          >
            <svg
              version="1.1"
              id="compass"
              xmlns="http://www.w3.org/2000/svg"
              // xmlns:xlink="http://www.w3.org/1999/xlink"
              x="0px"
              y="0px"
              width="100%"
              // height="200"
              viewBox="0 0 200 200"
              // xml:space="preserve"
            >
              <g transform="translate(19 -19)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  fill="#3e2723"
                  d="M162.312,118.21l-24.773,4.063   c-0.41,7.021-2.08,13.585-4.961,19.679c-2.871,6.094-6.787,11.466-11.581,16.075c-4.717,4.609-10.233,8.359-16.542,11.173   c-6.239,2.891-12.87,4.443-19.988,4.922l-2.881,23.341l-3.906-23.341c-7.509-0.479-14.471-2.031-20.779-4.922   c-6.318-2.813-11.757-6.642-16.386-11.251c-4.638-4.678-8.388-10.147-11.269-16.31c-2.792-6.172-4.472-12.735-4.873-19.835   L0,118.21l24.373-3.115c0.4-7.032,2.08-13.595,4.873-19.679c2.881-6.085,6.63-11.476,11.347-16.085   c4.717-4.61,10.234-8.282,16.542-11.085c6.318-2.725,13.192-4.375,20.545-4.766l3.906-23.966l3.125,23.741   c7.108,0.469,13.749,2.109,19.979,4.912c6.23,2.813,11.747,6.631,16.464,11.241c4.716,4.688,8.554,10.078,11.425,16.163   c2.881,6.162,4.551,12.646,4.961,19.522L162.312,118.21L162.312,118.21z M62.007,73.627c-6.22,3.126-11.581,6.798-15.976,11.017   c-4.482,4.219-8.065,9.141-10.868,14.825L58.901,109l0.879-1.406L46.832,84.409L71.84,96.666l0.566-0.234L62.007,73.627   L62.007,73.627z M58.901,129.372l-0.4-1.016l-23.495,9.142c5.351,12.021,13.974,20.763,25.809,25.997l10.556-22.56l-1.045-0.391   l-23.495,12.169L58.901,129.372L58.901,129.372z M126.992,100.104c-5.204-11.554-13.427-19.991-24.783-25.461l-11.024,22.18   l1.191,0.859l23.338-13.272l-12.47,24.044c0.254,0.234,0.488,0.469,0.645,0.781c0.078,0.234,0.322,0.625,0.566,1.016   L126.992,100.104L126.992,100.104z M90.94,140.779c-0.156,0.078-0.557,0.156-1.191,0.156l11.415,22.56   c11.123-5.156,19.511-13.429,25.184-24.747l-22.693-9.767l-0.645,0.781l12.704,22.951L90.94,140.779L90.94,140.779z M91.34,118.366   c0-2.725-0.879-4.913-2.793-6.71c-1.835-1.719-4.159-2.656-6.962-2.656c-2.871,0-5.341,0.938-7.343,2.656   c-2.08,1.797-3.037,3.985-3.037,6.71c0,2.891,0.957,5.313,3.037,7.412c2.002,2.109,4.472,3.203,7.343,3.203   c2.803,0,5.127-1.094,6.962-3.203C90.461,123.679,91.34,121.257,91.34,118.366L91.34,118.366z M101.574,74.018   c-2.479-0.928-5.038-1.719-7.675-2.412c-2.637-0.625-5.352-1.172-8.153-1.416l3.759,26.008c0,0.313,0.244,0.469,0.801,0.469   L101.574,74.018L101.574,74.018z M57.701,126.716l-26.854-4.053c0.557,5.303,1.914,10.147,4.16,14.601l23.25-9.298L57.701,126.716   L57.701,126.716z M76.791,167.792l-4.15-26.466l-0.645-0.391l-10.624,22.95c1.68,1.094,4.003,1.953,6.875,2.578   C71.206,167.089,73.998,167.479,76.791,167.792L76.791,167.792z M35.007,100.104c-2.402,5.693-3.759,10.381-4.16,14.21l27.41-3.673   l0.244-0.625L35.007,100.104L35.007,100.104z M88.87,141.795l-3.524,25.997c2.479-0.313,5.038-0.703,7.597-1.328   s5.029-1.484,7.353-2.578l-10.79-22.56C89.504,141.639,89.26,141.795,88.87,141.795L88.87,141.795z M130.498,123.444l-26.043,3.896   l-0.166,0.781l22.059,9.767c1.113-2.422,1.992-4.844,2.559-7.188C129.541,128.278,130.107,125.856,130.498,123.444L130.498,123.444   z M76.791,70.189c-2.793,0.244-5.195,0.635-7.343,1.104c-2.158,0.469-4.395,1.162-6.796,1.943l10.233,22.961L76.791,70.189   L76.791,70.189z M130.498,114.313c-0.322-2.579-0.635-4.923-1.123-7.032c-0.469-2.109-1.191-4.297-1.992-6.563l-22.459,9.922   L130.498,114.313L130.498,114.313z M131.298,98.229c-2.471-6.095-6.151-11.554-11.024-16.319   c-4.795-4.844-10.155-8.751-15.985-11.72l-1.279,2.666c11.825,6.241,20.458,15.069,26.053,26.613L131.298,98.229L131.298,98.229z    M61.216,71.439l-1.279-1.865c-6.64,2.412-12.392,6.084-17.264,10.851C37.8,85.269,34.05,90.972,31.248,97.682l2.714,1.006   C39.079,86.206,48.189,77.144,61.216,71.439L61.216,71.439z M128.183,139.529c-6.474,12.179-15.35,20.685-26.609,25.372l1.27,2.5   c12.704-6.016,22.06-14.834,28.055-26.622L128.183,139.529L128.183,139.529z M58.745,167.167l1.426-2.422   c-5.986-2.422-11.259-5.859-15.897-10.303c-4.717-4.376-8.154-9.679-10.556-15.929l-2.714,0.781   C36.677,152.177,45.953,161.464,58.745,167.167L58.745,167.167z M81.996,107.203c1.279,0.234,2.549,0.703,3.838,1.25   c1.348,0.469,2.313,1.094,3.036,1.797l5.029-4.922c-1.992-1.719-3.828-2.891-5.674-3.516c-1.757-0.625-3.837-1.016-6.229-1.094   V107.203L81.996,107.203z M80.794,100.719c-4.472,0.234-8.388,2.031-11.913,5.469l4.794,4.063c1.279-1.094,2.402-1.797,3.369-2.109   c0.957-0.391,2.148-0.703,3.75-0.938V100.719L80.794,100.719z M99.739,117.985c-0.322-2.344-0.811-4.376-1.523-6.173   c-0.635-1.875-1.836-3.75-3.516-5.625l-4.951,4.453c2.158,2.188,3.271,4.61,3.271,7.345H99.739L99.739,117.985z M99.739,119.382   H93.02c-0.234,1.641-0.479,2.891-0.723,3.896c-0.156,0.947-0.879,1.963-1.992,3.047l4.15,4.453   C97.415,127.966,99.172,124.147,99.739,119.382L99.739,119.382z M63.296,119.382c0.156,2.188,0.635,4.297,1.435,6.318   c0.879,2.031,2.07,3.828,3.76,5.469l4.15-4.453c-1.123-1.084-1.835-2.266-2.314-3.584c-0.4-1.25-0.566-2.5-0.566-3.75H63.296   L63.296,119.382z M81.996,136.248c2.393,0,4.385-0.391,6.151-1.172c1.758-0.859,3.594-2.031,5.518-3.516l-4.16-4.454   c-1.123,0.703-2.079,1.328-2.792,1.875c-0.801,0.469-2.324,1.016-4.717,1.406V136.248L81.996,136.248z M80.794,130.388   c-3.115,0-5.429-1.016-7.119-3.047l-4.384,4.454c1.67,1.328,3.427,2.422,5.195,3.281c1.836,0.781,3.906,1.172,6.308,1.172V130.388   L80.794,130.388z M69.76,117.985c0-2.734,0.957-5.157,2.88-7.345l-4.56-3.828c-1.436,1.719-2.471,3.438-3.35,5.156   c-0.8,1.719-1.279,3.673-1.435,6.017H69.76L69.76,117.985z M135.458,122.663l-3.125,0.615c-0.078,2.666-0.557,5.313-1.191,7.813   c-0.723,2.501-1.602,5.079-2.725,7.813l2.48,1.25C133.701,134.451,135.214,128.591,135.458,122.663L135.458,122.663z    M84.955,66.137l0.391,2.422c2.88,0.156,5.751,0.625,8.632,1.406c2.871,0.859,5.674,1.709,8.397,2.646l1.279-2.646   c-3.037-0.938-6.151-1.797-9.276-2.5C91.262,66.693,88.147,66.293,84.955,66.137L84.955,66.137z M76.791,169.043   c-5.273-0.548-10.625-1.954-15.976-4.142l-1.035,2.5c4.717,2.579,10.546,4.063,17.421,4.454L76.791,169.043L76.791,169.043z    M85.345,169.043l-0.635,2.813c3.202-0.156,6.151-0.547,8.876-1.328c2.705-0.781,5.43-1.72,8.222-2.97l-0.645-2.422   C96.135,167.479,90.862,168.729,85.345,169.043L85.345,169.043z M135.458,114.704c-0.088-2.657-0.557-5.313-1.201-7.892   c-0.723-2.578-1.592-5.234-2.715-7.959l-2.08,1.25c0.713,2.256,1.279,4.678,1.836,7.178c0.566,2.5,0.957,4.922,1.035,7.267   L135.458,114.704L135.458,114.704z M33.483,137.498c-1.601-3.281-2.636-5.859-2.949-7.735c-0.41-1.875-0.732-4.287-0.967-7.1   l-3.115-0.625c0.244,2.969,0.8,5.771,1.513,8.428c0.801,2.657,1.758,5.392,2.881,8.282L33.483,137.498L33.483,137.498z    M26.453,114.704l3.115-0.156c0.234-3.126,0.723-5.86,1.357-8.282c0.635-2.344,1.514-4.609,2.558-6.797l-2.48-1.24   C28.767,103.531,27.253,109,26.453,114.704L26.453,114.704z M77.201,66.137c-2.715,0.156-5.518,0.391-8.232,0.781   c-2.637,0.469-5.439,1.172-8.154,2.266l0.801,2.031c2.393-0.703,4.794-1.25,7.353-1.797c2.626-0.547,5.273-0.938,8.076-1.016   L77.201,66.137z"
                />
                <text
                  transform="matrix(1 0 0 1 77 37)"
                  fill="#3e2723"
                  fontFamily="Arial"
                  fontSize="14"
                >
                  N
                </text>
                <text
                  transform="matrix(1 0 0 1 77 37)"
                  x="89"
                  y="86"
                  fill="#3e2723"
                  fontFamily="Arial"
                  fontSize="14"
                >
                  E
                </text>
                <text
                  transform="matrix(1 0 0 1 77 37)"
                  x="-91"
                  y="85"
                  fill="#3e2723"
                  fontFamily="Arial"
                  fontSize="14"
                >
                  W
                </text>
                <text
                  transform="matrix(1 0 0 1 77 37)"
                  y="172"
                  fill="#3e2723"
                  fontFamily="Arial"
                  fontSize="14"
                >
                  S
                </text>
              </g>
            </svg>
          </Box>
          <Controls
            saveProject={this.saveProject.bind(this)}
            clearScene={this.clearScene.bind(this)}
            group={this.handleGroup.bind(this)}
            scene={this.scene}
            camera={this.camera}
            onCenter={this.handleCenter.bind(this)}
            onMove={this.handleMove.bind(this)}
            onFocus={this.handleFocus.bind(this)}
            onRotate={this.handleRotate.bind(this)}
            onDelete={this.handleDelete.bind(this)}
            items={nodeItems}
            loadObjects={this.loadObjects.bind(this)}
            canvasRef={canvasRef}
            replaceModel={this.replaceModel.bind(this)}
            duplicate={this.duplicate.bind(this)}
            state={{
              offsets: this.offsets,
            }}
            intensity={this.state.intensity.value}
            setIntensity={(intensity: number) =>
              this.setState({
                intensity: { value: intensity, changed: true },
              })
            }
            compassRef={this.props.compassRef}
            setColorRecursive={this.setColorRecursive.bind(this)}
            addGroup={this.addGroup.bind(this)}
            handleMirror={this.handleMirror.bind(this)}
            createCovering={this.createCovering.bind(this)}
            time = {this.state.time}
            setTime={(time: number) => {
              this.setState({ time });
              if(this.spotlight) {
                this.spotlight.position.x = 1000 * -time;
                this.spotlight.position.y = 3000 - Math.abs(-time * 100);
              }
            }
            }
            setColor={(color: number) => {
              if(this.spotlight) {
                this.spotlight.color = new Color(color);
              }
            }
            }
          />
          {preloaderVisible && <Loader />}
          <canvas ref={canvasRef}></canvas>
        </Box>
      </Box>
    );
  }
}

export default connector(withApollo<PreviewProps>(Preview));
